import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import CreateFolderModal from '../components/CreateFolderModal.jsx';
import EnterNameModal from '../components/EnterNameModal.jsx';
import { signInAnonymously } from 'firebase/auth';
import { ApiError, authApi, authApiIdempotent, authFetch, initFirebase, parseApiEnvelopeResponse, trackEvent, onAuth, getIdToken, waitForUserAvailable, waitForDbAvailable, getAuthInstance, uploadCardImage } from '../firebase/config.js';
import { Events } from '../firebase/events.js';
import { getDb } from '../firebase/config.js';
import { recordExamResult } from '../firebase/progress.js';
import { collection, getDocs, getDoc, setDoc, doc, serverTimestamp, updateDoc, deleteDoc, onSnapshot, getCountFromServer } from 'firebase/firestore';
import FlashcardViewer from '../components/FlashcardViewer.jsx';
import { useNavigate, useLocation } from 'react-router-dom';
import { useFlashFonic } from '../contexts/FlashFonicContext.js';
import { FlashFonicProvider } from '../contexts/FlashFonicContext.js';
import UploadControls from '../components/UploadControls.jsx';
import { useUploadPlayback } from '../hooks/useUploadPlayback.js';
import GamesModal from '../components/GamesModal.jsx';
import GameViewer from '../components/GameViewer.jsx';
import NemesisGame from '../components/NemesisGame.jsx';
import FlashNotesActionModal from '../components/FlashNotesActionModal.jsx';
import FlashNotesViewer from '../components/FlashNotesViewer.jsx';
import Modal from '../components/ui/Modal.jsx';
import ExamViewer from '../components/ExamViewer.jsx';
import { useExams } from '../hooks/useExams.js';
import { formatAutoFlashInterval, sliderToInterval, intervalToSlider } from '../utils/time.js';
import { buildCardKeySet, partitionUniqueCards } from '../utils/folders.js';
import ConfirmModal from '../components/ConfirmModal.jsx';
import ExamCreateModal from '../components/ExamCreateModal.jsx';
import ExamHistoryModal from '../components/ExamHistoryModal.jsx';
import ExamQueuePill from '../components/ExamQueuePill.jsx';
import FeedbackModal from '../components/FeedbackModal.jsx';
import CreditConversionModal from '../components/CreditConversionModal.jsx';
import EditableCardContent from '../components/EditableCardContent.jsx';
import { renderContentForPdf, exportNotesToPDF } from '../utils/pdf.js';
import { buildFolderPdf } from '../utils/pdfFolder.js';
import { ENV } from '../config/env.js';
import FlashClinicalWorkspace from '../components/clinical/FlashClinicalWorkspace.jsx';
import FonicLiveCapture from '../components/FonicLiveCapture.jsx';
import { DEFAULT_CLINICAL_SETTINGS, CLINICAL_TIER_LIMITS, CLINICAL_CARD_STYLES, resolveClampedExamType } from '../components/clinical/clinicalOptions.js';
import { enrichClinicalCard, isClinicalQueueCard, clinicalCardLinkFields } from '../components/clinical/clinicalCardUtils.js';
import ClinicalGeneratedCards from '../components/clinical/ClinicalGeneratedCards.jsx';
import AppHomeFoldersSection from '../components/AppHomeFoldersSection.jsx';
import ImportModal from '../components/ImportModal.jsx';
import ClinicalDocumentStudyModal from '../components/clinical/ClinicalDocumentStudyModal.jsx';
import RestoreBackupModal from '../components/RestoreBackupModal.jsx';
import { exportBackupZip, getBackupImageBlob } from '../utils/backup.js';
import { exportFolderToAnki } from '../utils/ankiExport.js';
import { cacheFolderList, cacheFolderCards, getCachedFolderList, getCachedFolderCards } from '../utils/offlineCache.js';
import { isOfficeFile, convertOfficeFileToPdf } from '../utils/officeConversion.js';
import { useOnlineStatus } from '../hooks/useOnlineStatus.js';
import ShareFolderModal from '../components/ShareFolderModal.jsx';
import AppHomeReviewQueue from '../components/AppHomeReviewQueue.jsx';
import OnboardingWizard, { isOnboardingNeeded, completeOnboarding, getOnboardingProfile } from '../components/OnboardingWizard.jsx';
import PromotionalModal, { trackGenerationForPromo } from '../components/PromotionalModal.jsx';
import FreeTrialBanner, { getFreeQuestionsUsed, hasFreeQuestionsRemaining, incrementFreeQuestionsUsed, FREE_TRIAL_TOTAL } from '../components/FreeTrialBanner.jsx';
import ClinicalGateModal from '../components/ClinicalGateModal.jsx';
import PerformanceDashboard, { addXP, recordStudyToday, XP_PER_CARD, XP_PER_EXAM } from '../components/PerformanceDashboard.jsx';
import SettingsModal from '../components/SettingsModal.jsx';
import HomeDashboard from '../components/HomeDashboard.jsx';
import MobileBottomNav from '../components/MobileBottomNav.jsx';

const TIER_BADGES = {
    free: { label: 'Free', className: 'text-slate-300 bg-white/10' },
    basic: { label: 'Basic', className: 'text-blue-300 bg-blue-500/15' },
    pro: { label: 'Pro', className: 'text-teal-300 bg-teal-500/15' },
    elite: { label: 'Elite', className: 'text-amber-300 bg-amber-500/15' },
    class_prep: { label: 'Class Prep', className: 'text-teal-300 bg-teal-500/15' },
    board_review: { label: 'Board Review', className: 'text-emerald-300 bg-emerald-500/15' },
    specialty_boards: { label: 'Specialty Boards', className: 'text-sky-300 bg-sky-500/15' },
};
const getTierBadge = (tier) => TIER_BADGES[tier] || TIER_BADGES.free;

const SIGNUP_STORAGE_KEY = 'ff:signupFunnelShown';
const SIGNUP_GENERATION_THRESHOLD = 2;
const TOTAL_GENERATIONS_KEY = 'ff:totalGenerations'; // Track total generations for anonymous users
const USAGE_STORAGE_KEY = 'flashfonic-usage';
const getUsagePeriodKey = () => {
    const now = new Date();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    return `${now.getFullYear()}-${month}`;
};

// Helper to track total generations and check if signup popup should show
const trackGenerationAndCheckSignup = (increment = 1) => {
    try {
        const stored = localStorage.getItem(TOTAL_GENERATIONS_KEY);
        const currentTotal = Number(stored || 0);
        const newTotal = currentTotal + increment;
        localStorage.setItem(TOTAL_GENERATIONS_KEY, String(newTotal));
        
        const signupShown = localStorage.getItem(SIGNUP_STORAGE_KEY) === '1';
        const shouldShow = !signupShown && currentTotal < SIGNUP_GENERATION_THRESHOLD && newTotal >= SIGNUP_GENERATION_THRESHOLD;
        
        return { shouldShow, newTotal };
    } catch (_) {
        return { shouldShow: false, newTotal: 0 };
    }
};

const AppHome = ({
    isDevMode,
    entitlements,
    refreshEntitlements,
    onOpenAccount,
    onOpenPlanChooser,
    onSelectFoto,
}) => {
    const API_BASE_URL = ENV.apiBaseUrl;
    useEffect(() => {
        try { trackEvent('onboarding_step', { step: 'app_home_view' }); } catch (_) {}
        try { /* pixels are initialized globally; no-op if absent */ } catch (_) {}
    }, []);
    const navigate = useNavigate();
    const location = useLocation();
    const [remaining, setRemaining] = useState(0);
    const [user, setUser] = useState(null);
    const [folders, setFolders] = useState([]);
    const [sortBy, setSortBy] = useState('name'); // name | updated | created
    const [isCreateOpen, setIsCreateOpen] = useState(false);
    const [renameId, setRenameId] = useState(null);
    const [previewFolder, setPreviewFolder] = useState(null);
    const [previewSelected, setPreviewSelected] = useState({});
    const [previewMoveDestId, setPreviewMoveDestId] = useState('');
    const [previewSearch, setPreviewSearch] = useState('');
    const [previewFilter, setPreviewFilter] = useState('all'); // all | flagged
    const [lastMoveDestId, setLastMoveDestId] = useState('');
    const [lastMove, setLastMove] = useState(null);
    const [moveError, setMoveError] = useState(null);
    const [mobileMoveMenuOpen, setMobileMoveMenuOpen] = useState(false);
    const [previewEditingCardId, setPreviewEditingCardId] = useState(null);
    const signupPromptSeenRef = useRef(false);
    useEffect(() => {
        try { signupPromptSeenRef.current = localStorage.getItem(SIGNUP_STORAGE_KEY) === '1'; }
        catch (_) { signupPromptSeenRef.current = false; }
    }, []);

    // Settings modal
    const [showSettingsModal, setShowSettingsModal] = useState(false);
    // Performance dashboard
    const [showDashboard, setShowDashboard] = useState(false);
    // Onboarding wizard
    const [showOnboarding, setShowOnboarding] = useState(() => isOnboardingNeeded());
    // Promotional modal
    const [showPromoModal, setShowPromoModal] = useState(false);
    const [promoVariant, setPromoVariant] = useState('enjoying');
    // Free trial board questions
    const [freeQuestionsUsed, setFreeQuestionsUsed] = useState(() => getFreeQuestionsUsed());
    const [showClinicalGate, setShowClinicalGate] = useState(false);
    const [productMode, setProductMode] = useState('clinical'); // 'foto' | 'fonic' | 'clinical'
    const isClinicalTrialExhausted = freeQuestionsUsed >= FREE_TRIAL_TOTAL;

    // Refresh free-trial count and show gate when entering clinical mode with exhausted trial.
    // The trial counter is a local, lifetime, per-browser count with no concept of paid
    // entitlements — it must never gate a user who already has a real (paid or comped) tier,
    // regardless of how many lifetime generations that browser has ever made.
    useEffect(() => {
        const used = getFreeQuestionsUsed();
        setFreeQuestionsUsed(used);
        const hasPaidTier = !!(entitlements?.tier && entitlements.tier !== 'free');
        if (productMode === 'clinical' && used >= FREE_TRIAL_TOTAL && !hasPaidTier) {
            setShowClinicalGate(true);
        }
    }, [productMode, entitlements?.tier]);

    // Pick up onboarding flag set by LoginModal in the same SPA session
    useEffect(() => {
        const handler = () => setShowOnboarding(true);
        window.addEventListener('ff:onboarding:needed', handler);
        return () => window.removeEventListener('ff:onboarding:needed', handler);
    }, []);

    // Open performance dashboard from AppHeader
    useEffect(() => {
        const handler = () => setShowDashboard(true);
        window.addEventListener('ff:open:dashboard', handler);
        return () => window.removeEventListener('ff:open:dashboard', handler);
    }, []);

    const deleteFolderAndCards = async (folderId) => {
        try {
            const db = getDb();
            const currentUid = getCurrentUid();
            if (!db || !currentUid || !folderId) return;
            const cardsSnap = await getDocs(collection(db, 'users', currentUid, 'folders', folderId, 'cards'));
            for (const cd of cardsSnap.docs) {
                try { await deleteDoc(doc(db, 'users', currentUid, 'folders', folderId, 'cards', cd.id)); } catch (_) { }
            }
            try { await deleteDoc(doc(db, 'users', currentUid, 'folders', folderId)); } catch (_) { }
        } catch (_) { }
        setFolders(prev => prev.filter(f => f.id !== folderId));
    };

    const moveSelectedCardsFromPreview = async () => {
        const selectedIds = Object.keys(previewSelected).filter(id => previewSelected[id]);
        if (!previewFolder || selectedIds.length === 0 || !previewMoveDestId) return;
        try {
            const db = getDb();
            const currentUid = getCurrentUid();
            if (!db || !currentUid) return;
            const toMove = (previewFolder.cards || []).filter(c => selectedIds.includes(String(c.id)));
            const written = [];
            const movedSourceIds = new Set();
            let failed = 0;
            const destCards = await fetchCardsForFolder(previewMoveDestId);
            const existing = buildCardKeySet(destCards || []);
            const { unique: uniqueCandidates, duplicates } = partitionUniqueCards(toMove, existing);
            for (const c of uniqueCandidates) {
                try {
                    const cid = `${Date.now()}.${Math.floor(Math.random() * 10000)}`;
                    const payload = {
                        question: c.question || '',
                        answer: c.answer || '',
                        isFlagged: !!c.isFlagged,
                        lastViewed: c.lastViewed || null,
                        createdAt: serverTimestamp(),
                        ...clinicalCardLinkFields(c)
                    };
                    await setDoc(doc(db, 'users', currentUid, 'folders', previewMoveDestId, 'cards', cid), payload);
                    written.push({ destId: cid, payload });
                    movedSourceIds.add(String(c.id));
                } catch (_) {
                    failed += 1;
                }
            }

            const movedCount = written.length;
            setFolders(current => current.map(f => {
                if (f.id === previewFolder.id) {
                    return {
                        ...f,
                        cards: (f.cards || []).filter(c => !movedSourceIds.has(String(c.id))),
                        cardCount: Math.max(0, Number(f.cardCount ?? 0) - movedCount),
                        updatedAt: Date.now()
                    };
                }
                if (f.id === previewMoveDestId) {
                    return {
                        ...f,
                        cardCount: Number(f.cardCount ?? 0) + movedCount,
                        updatedAt: Date.now()
                    };
                }
                return f;
            }));
            setPreviewFolder(prev => prev ? { ...prev, cards: (prev.cards || []).filter(c => !movedSourceIds.has(String(c.id))) } : prev);
            try {
                const db2 = getDb();
                if (db2 && currentUid && previewFolder?.id) {
                    updateDoc(doc(db2, 'users', currentUid, 'folders', previewFolder.id), { updatedAt: serverTimestamp() }).catch(() => { });
                }
                if (db2 && currentUid && previewMoveDestId) {
                    updateDoc(doc(db2, 'users', currentUid, 'folders', previewMoveDestId), { updatedAt: serverTimestamp() }).catch(() => { });
                }
            } catch (_) { }
            setPreviewSelected({});
            setLastMoveDestId(previewMoveDestId);
            setLastMove({ srcFolderId: previewFolder.id, destFolderId: previewMoveDestId, items: written });
            setMoveError(failed > 0 ? `${failed} item${failed === 1 ? '' : 's'} failed to move` : null);
            pushToast(movedCount ? 'success' : 'info', `Moved ${movedCount} card${movedCount === 1 ? '' : 's'}${duplicates ? `, skipped ${duplicates} duplicate${duplicates === 1 ? '' : 's'}` : ''}${failed ? `, ${failed} failed` : ''}`);
        } catch (_) { }
    };

    const undoLastMove = async () => {
        if (!lastMove) return;
        if (lastMove.mode === 'local' && lastMove.local) {
            try {
                const { srcFolderId, destFolderId, local } = lastMove;
                const sourceCards = Array.isArray(local.sourceCards) ? local.sourceCards : [];
                const destCards = Array.isArray(local.destCards) ? local.destCards : [];
                const destIdSet = new Set(destCards.map(c => String(c.id)));
                setFolders(curr => curr.map(f => {
                    if (f.id === destFolderId) {
                        const nextCards = (f.cards || []).filter(c => !destIdSet.has(String(c.id)));
                        return { ...f, cards: nextCards, cardCount: nextCards.length, updatedAt: Date.now() };
                    }
                    if (f.id === srcFolderId) {
                        const nextCards = [...(f.cards || []), ...sourceCards];
                        return { ...f, cards: nextCards, cardCount: nextCards.length, updatedAt: Date.now() };
                    }
                    return f;
                }));
                if (previewFolder && previewFolder.id === srcFolderId) {
                    setPreviewFolder(prev => prev ? { ...prev, cards: [...(prev.cards || []), ...sourceCards] } : prev);
                }
                if (previewFolder && previewFolder.id === destFolderId) {
                    setPreviewFolder(prev => prev ? { ...prev, cards: (prev.cards || []).filter(c => !destIdSet.has(String(c.id))) } : prev);
                }
                setLastMove(null);
                setMoveError(null);
                pushToast('success', 'Move undone');
            } catch (_) {
                pushToast('error', 'Undo failed');
            }
            return;
        }
        try {
            const db = getDb();
            const currentUid = getCurrentUid();
            if (!db || !currentUid) return;
            const restored = [];
            for (const item of lastMove.items || []) {
                try { await deleteDoc(doc(db, 'users', currentUid, 'folders', lastMove.destFolderId, 'cards', item.destId)); } catch (_) { }
                const newId = `${Date.now()}.${Math.floor(Math.random() * 10000)}`;
                await setDoc(doc(db, 'users', currentUid, 'folders', lastMove.srcFolderId, 'cards', newId), {
                    question: item.payload.question || '',
                    answer: item.payload.answer || '',
                    isFlagged: !!item.payload.isFlagged,
                    lastViewed: item.payload.lastViewed || null,
                    createdAt: serverTimestamp(),
                    ...clinicalCardLinkFields(item.payload)
                });
                restored.push({ id: newId, ...item.payload });
            }
            if (previewFolder && previewFolder.id === lastMove.srcFolderId) {
                setPreviewFolder(prev => prev ? { ...prev, cards: [...(prev.cards || []), ...restored] } : prev);
                setFolders(curr => curr.map(f => f.id === lastMove.srcFolderId ? { ...f, cards: [...(f.cards || []), ...restored] } : f));
            }
            setLastMove(null);
            setMoveError(null);
            pushToast('success', 'Move undone');
        } catch (_) { pushToast('error', 'Undo failed'); }
    };

    const duplicateSelectedCardsInPreview = async () => {
        const selectedIds = Object.keys(previewSelected).filter(id => previewSelected[id]);
        if (!previewFolder || selectedIds.length === 0) return;
        try {
            const db = getDb();
            const currentUid = getCurrentUid();
            if (!db || !currentUid) { pushToast('error', 'Please sign in to save'); return; }
            const toDuplicate = (previewFolder.cards || []).filter(c => selectedIds.includes(String(c.id)));
            let success = 0;
            for (const c of toDuplicate) {
                try {
                    const cid = `${Date.now()}.${Math.floor(Math.random() * 10000)}`;
                    const payload = { question: c.question || '', answer: c.answer || '', isFlagged: !!c.isFlagged, lastViewed: c.lastViewed || null, createdAt: serverTimestamp(), ...clinicalCardLinkFields(c) };
                    await setDoc(doc(db, 'users', currentUid, 'folders', previewFolder.id, 'cards', cid), payload);
                    success += 1;
                } catch (_) { }
            }
            // Optimistic: append duplicates to local state for current preview folder
            if (success > 0) {
                setFolders(curr => curr.map(f => f.id === previewFolder.id ? { ...f, cards: [...(f.cards || []), ...toDuplicate.map(c => ({ ...c, id: `${Date.now()}.${Math.floor(Math.random() * 10000)}` }))] } : f));
                setPreviewFolder(prev => prev ? { ...prev, cards: [...(prev.cards || []), ...toDuplicate.map(c => ({ ...c, id: `${Date.now()}.${Math.floor(Math.random() * 10000)}` }))] } : prev);
            }
            pushToast(success ? 'success' : 'info', `Duplicated ${success} card${success === 1 ? '' : 's'}`);
        } catch (_) { pushToast('error', 'Duplicate failed'); }
    };

    const deleteSelectedCardsFromPreview = async () => {
        const selectedIds = Object.keys(previewSelected).filter(id => previewSelected[id]);
        if (!previewFolder || selectedIds.length === 0) return;
        try {
            const db = getDb();
            const currentUid = getCurrentUid();
            const toDelete = (previewFolder.cards || []).filter(c => selectedIds.includes(String(c.id)));
            let success = 0;
            if (!db || !currentUid) return;
            for (const c of toDelete) {
                try { await deleteDoc(doc(db, 'users', currentUid, 'folders', previewFolder.id, 'cards', String(c.id))); success += 1; } catch (_) { }
            }
            if (success > 0) {
                setFolders(curr => curr.map(f => f.id === previewFolder.id ? { ...f, cards: (f.cards || []).filter(c => !selectedIds.includes(String(c.id))) } : f));
                setPreviewFolder(prev => prev ? { ...prev, cards: (prev.cards || []).filter(c => !selectedIds.includes(String(c.id))) } : prev);
                setPreviewSelected({});
            }
            pushToast(success ? 'success' : 'info', `Deleted ${success} card${success === 1 ? '' : 's'}`);
        } catch (_) { pushToast('error', 'Delete failed'); }
    };
    const [menuOpenId, setMenuOpenId] = useState(null);
    const [isSortOpen, setIsSortOpen] = useState(false);
    // FlashFoto states
    const fileInputRef = useRef(null);
    const [imagePreview, setImagePreview] = useState(null);
    // Set instead of imagePreview when the uploaded file is a document (PDF/DOCX/PPTX/XLSX)
    // rather than an image — there's no visual preview for these, just a filename chip.
    const [documentFileName, setDocumentFileName] = useState(null);
    // PDFs — and Word/PPT/Excel, converted server-side to PDF first (see
    // convertOfficeFileToPdf) — open the full-document study modal (scroll, highlight,
    // generate) instead of the inline uploader. This holds the raw File while that modal
    // is open.
    const [documentStudyFile, setDocumentStudyFile] = useState(null);
    const [showDocumentStudyModal, setShowDocumentStudyModal] = useState(false);
    // Reopening a previously-saved source document (e.g. via a card's "View source" link)
    // instead of studying a freshly uploaded file — mirrors DeckPage's sourceDocument/
    // initialPage props on ClinicalDocumentStudyModal.
    const [studyDocument, setStudyDocument] = useState(null);
    const [documentStudyInitialPage, setDocumentStudyInitialPage] = useState(null);
    const [dragActive, setDragActive] = useState(false);
    const [aiAnalysis, setAiAnalysis] = useState(null);
    const [cardCount, setCardCount] = useState(5);
    const [showFotoAIModal, setShowFotoAIModal] = useState(false);
    const [fotoAIRec, setFotoAIRec] = useState(null);
    const [isGenerating, setIsGenerating] = useState(false);
    const lastBase64Ref = useRef(null);
    const [statusMsg, setStatusMsg] = useState('');
    const [studyingFolder, setStudyingFolder] = useState(null);
    const [showGamesModal, setShowGamesModal] = useState(null);
    const [gameModeFolder, setGameModeFolder] = useState(null);
    const [nemesisModeFolder, setNemesisModeFolder] = useState(null);
    const [mostRecentScore, setMostRecentScore] = useState(null);
    const [isGeneratingNotes, setIsGeneratingNotes] = useState(false);
    const [flashNotesContent, setFlashNotesContent] = useState(null);
    const [showFlashNotesViewer, setShowFlashNotesViewer] = useState(false);
    // Use global DocViewer in App.jsx
    const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
    const [examWizardState, setExamWizardState] = useState(null);
    const [examSelectedFolderIds, setExamSelectedFolderIds] = useState({});
    const [showExamCreate, setShowExamCreate] = useState(false);
    const [examPreSelectedFolderId, setExamPreSelectedFolderId] = useState(null);
    const [activeExam, setActiveExam] = useState(null);
    const [savedExams, setSavedExams] = useState([]);
    const [examHistory, setExamHistory] = useState([]);
    const [pendingExamSave, setPendingExamSave] = useState(null);
    const [examQueue, setExamQueue] = useState(null); // null | { status: 'creating' | 'ready', exam: null | object }

    // DeckPage's "Exams" button navigates here with state.openExam set to a folder id
    // (it has no local exam-creation UI of its own). Pick that up and open ExamCreateModal
    // pre-scoped to that folder, then clear the state so back/forward doesn't re-trigger it.
    useEffect(() => {
        const folderId = location.state?.openExam;
        if (!folderId) return;
        setExamPreSelectedFolderId(folderId);
        setShowExamCreate(true);
        navigate(location.pathname, { replace: true, state: {} });
    }, [location.state]);
    const [pendingRename, setPendingRename] = useState(null);
    const [toasts, setToasts] = useState([]);
    const [showImportModal, setShowImportModal] = useState(false);
    const [showRestoreBackupModal, setShowRestoreBackupModal] = useState(false);
    const [isExportingBackup, setIsExportingBackup] = useState(false);
    const [shareFolderTarget, setShareFolderTarget] = useState(null);
    const [homeQueued, setHomeQueued] = useState([]); // Unified Review Queue
    const homeQueuedCountRef = useRef(0);
    useEffect(() => {
        const prev = Number(homeQueuedCountRef.current || 0);
        const next = Number(homeQueued.length || 0);
        if (next > prev) {
            const inc = next - prev;
            try { window.dispatchEvent(new CustomEvent('ff:cardsGenerated', { detail: { increment: inc } })); } catch (_) {}
        }
        homeQueuedCountRef.current = next;
    }, [homeQueued.length]);
    const [showGuestSignupPrompt, setShowGuestSignupPrompt] = useState(false);
    const [selectedQueue, setSelectedQueue] = useState({});

    // Local generation counter — used only for the guest-signup nudge (prompt
    // anonymous users to create an account after their first couple of generations)
    // and as a display fallback before entitlements loads. The real credit limit is
    // enforced server-side against entitlements.creditsBalance (atomic debit/refund,
    // see generateFlashcardRequestLive et al.) and surfaced via toast + the Clinicals
    // free-trial gate (FreeTrialBanner/ClinicalGateModal) — this counter no longer
    // triggers its own separate "limit reached" modal.
    const bumpHomeUsage = useCallback((increment = 1) => {
        const inc = Number(increment) || 0;
        if (inc <= 0) return;
        setHomeUsage(prev => {
            const nextCount = (prev.count || 0) + inc;
            const next = { ...prev, count: nextCount };
            try { localStorage.setItem(USAGE_STORAGE_KEY, JSON.stringify(next)); } catch (_) {}
            // Check if user is anonymous (not just if they don't have entitlements)
            const isGuest = (() => {
                try {
                    const auth = getAuthInstance();
                    if (auth && auth.currentUser) return !!auth.currentUser.isAnonymous;
                } catch (_) {}
                return (entitlements?.uid || 'anonymous') === 'anonymous';
            })();
            // Track total generations and check if signup popup should show
            if (isGuest) {
                const { shouldShow } = trackGenerationAndCheckSignup(inc);
                if (shouldShow) {
                    signupPromptSeenRef.current = true;
                    try { localStorage.setItem(SIGNUP_STORAGE_KEY, '1'); } catch (_) {}
                    setShowGuestSignupPrompt(true);
                }
            }
            return next;
        });
    }, [entitlements?.uid]);
    const [editingCardId, setEditingCardId] = useState(null);
    const [pendingDeleteId, setPendingDeleteId] = useState(null);
    const homeQueueRef = useRef(null);
    const pushToast = (kind, text) => {
        const id = Date.now() + Math.random();
        setToasts((t) => [...t, { id, kind, text }]);
        setTimeout(() => setToasts((t) => t.filter(x => x.id !== id)), 3200);
    };
    // Backend-driven exam creation (shared with MainApp via useExams). Debit happens
    // server-side inside POST /api/exams — no client pre-debit.
    const { createFromCards: createExamFromCards, createClinical } = useExams({ notify: pushToast, refreshEntitlements });
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    const [homeUsage, setHomeUsage] = useState({ count: 0, limit: 30, period: getUsagePeriodKey() });
    const [uid, setUid] = useState(null);
    const isOnline = useOnlineStatus();
    
    // Helper to get current uid (from state or auth instance) - works for anonymous users too
    // Must be defined after uid is declared
    const getCurrentUid = useCallback(() => {
        if (uid) return uid;
        try {
            const auth = getAuthInstance();
            const currentUser = auth?.currentUser;
            return currentUser?.uid || null;
        } catch (err) {
            console.error('[getCurrentUid] Error:', err);
            return null;
        }
    }, [uid]);
    
    // Track authenticated user for header display
    useEffect(() => {
        let unsub = null;
        (async () => {
            await initFirebase();
            unsub = onAuth((u) => {
                setUser(u && !u.isAnonymous ? u : null);
            });
        })();
        return () => {
            if (unsub) {
                try { unsub(); } catch (_) {}
            }
        };
    }, []);
    // Prevent browser from navigating on stray file drops anywhere on the window
    useEffect(() => {
        const prevent = (e) => {
            try {
                const types = e?.dataTransfer?.types ? Array.from(e.dataTransfer.types) : [];
                if (!types.includes('Files')) return;
            } catch (_) { return; }
            e.preventDefault();
            e.stopPropagation();
        };
        try {
            window.addEventListener('dragover', prevent, { passive: false });
            window.addEventListener('drop', prevent, { passive: false });
        } catch (_) { }
        return () => {
            try {
                window.removeEventListener('dragover', prevent);
                window.removeEventListener('drop', prevent);
            } catch (_) { }
        };
    }, []);
    useEffect(() => {
        try {
            const period = getUsagePeriodKey();
            const storedJson = localStorage.getItem(USAGE_STORAGE_KEY);
            if (storedJson) {
                const stored = JSON.parse(storedJson);
                const storedPeriod = stored?.period || (stored?.date ? String(stored.date).slice(0, 7) : null);
                if (storedPeriod === period) {
                    setHomeUsage({ ...stored, period });
                } else {
                    const fresh = { count: 0, limit: stored?.limit || 30, period };
                    localStorage.setItem(USAGE_STORAGE_KEY, JSON.stringify(fresh));
                    setHomeUsage(fresh);
                }
            } else {
                const fresh = { count: 0, limit: 30, period };
                localStorage.setItem(USAGE_STORAGE_KEY, JSON.stringify(fresh));
                setHomeUsage(fresh);
            }
        } catch (_) { }
    }, []);
    // Saved exams/history (local persistence in page)
    useEffect(() => {
        try {
            const se = JSON.parse(localStorage.getItem('ff:savedExams') || '[]');
            if (Array.isArray(se)) setSavedExams(se);
        } catch (_) { }
        try {
            const eh = JSON.parse(localStorage.getItem('ff:examHistory') || '[]');
            if (Array.isArray(eh)) setExamHistory(eh);
        } catch (_) { }
    }, []);
    // Realtime subscribe to saved exams in Firestore (cross-device)
    useEffect(() => {
        let cleanup = null;
        (async () => {
            try {
                await waitForUserAvailable(3000);
                const db = getDb();
                const currentUid = getCurrentUid();
                if (!db || !currentUid) return;
                cleanup = onSnapshot(
                    collection(db, 'users', currentUid, 'savedExams'),
                    (snap) => {
                        const list = snap.docs.map(d => ({ id: d.id, ...(d.data() || {}) }));
                        setSavedExams(list);
                    },
                    (err) => {
                        console.warn('savedExams listener error:', err?.code || err);
                    }
                );
            } catch (_) { }
        })();
        return () => { try { cleanup && cleanup(); } catch (_) { } };
    }, [uid, getCurrentUid]);

    // Remove legacy imports from localStorage; Firestore is the sole source of truth for folders
    useEffect(() => {
        try { localStorage.setItem('ff:savedExams', JSON.stringify(savedExams)); } catch (_) { }
    }, [savedExams]);
    useEffect(() => {
        try { localStorage.setItem('ff:examHistory', JSON.stringify(examHistory)); } catch (_) { }
    }, [examHistory]);

    // Ensure Firebase (and Firestore) is initialized
    useEffect(() => { initFirebase().catch(() => { }); }, []);
    // Track auth user id for user-scoped Firestore paths
    useEffect(() => {
        try {
            const unsub = onAuth((user) => { setUid(user ? user.uid : null); });
            return () => { try { unsub && unsub(); } catch (_) { } };
        } catch (_) { }
    }, []);
    const flashFonicNotify = (msg) => { setStatusMsg(msg); if (msg) pushToast('info', msg); };
    // Hoist upload playback so it doesn't remount on panel re-renders
    const upload = useUploadPlayback({ setNotification: flashFonicNotify });
    const generateFlashcardRequestLive = async (requestBody) => {
        try {
            // Coarse pre-check only — avoids burning a transcription call for a user who's
            // already at zero. Not exact insufficient-credit detection: the real cost (1
            // credit + audio minutes, or the tiered clinical cost) is computed and debited
            // atomically server-side inside /generate-flashcard, which also refunds in full
            // if generation fails. Credits are never pre-debited client-side anymore.
            if (!isDevMode && Number(entitlements?.creditsBalance ?? 0) <= 0) {
                pushToast('error', 'Not enough credits');
                return;
            }
            pushToast('info', 'Generating card from audio...');
            const data = await authApiIdempotent(`${API_BASE_URL}/generate-flashcard`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(requestBody) });
            if (typeof refreshEntitlements === 'function') { try { await refreshEntitlements(); } catch (_) { } }
            bumpHomeUsage(1);
            try { trackEvent(Events.generation_live); } catch { }
            // Save into selected/new folder; don't auto-open study on mobile
            const now = Date.now();
            const baseCard = { id: now, question: data.question || 'New card', answer: data.answer || data, isFlagged: false, lastViewed: null };
            const card = requestBody?.clinical
                ? enrichClinicalCard(baseCard, clinicalSettings, data)
                : baseCard;
            const isDuplicate = homeQueued.some(c =>
                (c.question || '').trim() === (card.question || '').trim() &&
                (c.answer || '').trim() === (card.answer || '').trim()
            );

            const allowDuplicateCardsInQueue = isDevMode || (new URLSearchParams(window.location.search).get('dev') === 'true');
            if (!allowDuplicateCardsInQueue && isDuplicate) {
                pushToast('info', 'Card generated, but skipped as a duplicate in the Review Queue.');
                return;
            }

            // Add to local Review Queue; no auto-scroll to prevent page jump
            setHomeQueued(prev => [card, ...prev]);
            // Do not auto-create or auto-save into a folder; keep in Review Queue
            pushToast('success', 'Card generated. Edit and move from the Review Queue.');
        } catch (e) {
            console.error('[AppHome] generateFlashcardRequestLive error:', { code: e?.code, requestId: e?.requestId, status: e?.status });
            pushToast('error', e.message || 'Generation failed');
        }
    };
    // camera
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const [isCameraOn, setIsCameraOn] = useState(false);
    const [cameraErr, setCameraErr] = useState('');
    // target folder selection
    const [targetFolderId, setTargetFolderId] = useState('');
    const [isMovingQueueCards, setIsMovingQueueCards] = useState(false);
    const isMovingQueueCardsRef = useRef(false);

    const [searchQuery, setSearchQuery] = useState('');
    const [clinicalInputType, setClinicalInputType] = useState('image');
    const [clinicalSettings, setClinicalSettings] = useState(() => {
        try {
            const raw = localStorage.getItem('ff:clinicalSettings');
            if (raw) {
                const parsed = JSON.parse(raw);
                // Migrate a cardStyle saved before the Card Style consolidation (e.g. "Management
                // Algorithm", "Mechanism / Pathophysiology") back to the default rather than
                // silently sending a retired style the backend no longer recognizes.
                if (parsed.cardStyle && !CLINICAL_CARD_STYLES.includes(parsed.cardStyle)) {
                    parsed.cardStyle = DEFAULT_CLINICAL_SETTINGS.cardStyle;
                }
                return { ...DEFAULT_CLINICAL_SETTINGS, ...parsed };
            }
            // First visit — seed examType from onboarding profile, clamped to what the
            // user's current entitlement tier actually allows (never send an examType
            // above their paid tier just because that's what they said their goal was).
            const profile = getOnboardingProfile();
            let examType = DEFAULT_CLINICAL_SETTINGS.examType;
            if (profile) {
                const { examType: clamped } = resolveClampedExamType({
                    examTarget: profile.examTarget,
                    subjects: profile.subjects,
                    isMultiSubject: profile.focus === 'class_prep',
                    entitlementsTier: entitlements?.tier,
                });
                examType = clamped || DEFAULT_CLINICAL_SETTINGS.examType;
            }
            return { ...DEFAULT_CLINICAL_SETTINGS, examType };
        } catch (_) {
            return { ...DEFAULT_CLINICAL_SETTINGS };
        }
    });
    const [clinicalNotes, setClinicalNotes] = useState('');
    const [isClinicalGenerating, setIsClinicalGenerating] = useState(false);
    // A pasted screenshot attached to the card being composed in the Type flow — never
    // analyzed, just uploaded and displayed back on the resulting card (see plan: paste an
    // image alongside a typed vignette, image rides along, no vision/OCR involved).
    const [clinicalPastedImage, setClinicalPastedImage] = useState(null); // File
    const [clinicalPastedImagePreviewUrl, setClinicalPastedImagePreviewUrl] = useState(null);

    const handleClinicalNotesPaste = (e) => {
        const items = e.clipboardData?.items;
        if (!items) return;
        for (const item of items) {
            if (item.type && item.type.startsWith('image/')) {
                const file = item.getAsFile();
                if (!file) continue;
                e.preventDefault();
                setClinicalPastedImagePreviewUrl((prev) => {
                    if (prev) { try { URL.revokeObjectURL(prev); } catch (_) {} }
                    return URL.createObjectURL(file);
                });
                setClinicalPastedImage(file);
                break;
            }
        }
    };

    const clearClinicalPastedImage = () => {
        setClinicalPastedImagePreviewUrl((prev) => {
            if (prev) { try { URL.revokeObjectURL(prev); } catch (_) {} }
            return null;
        });
        setClinicalPastedImage(null);
    };
    useEffect(() => {
        const limit = Number(homeUsage?.limit || 30);
        const count = Number(homeUsage?.count || 0);
        setRemaining(Math.max(0, limit - count));
    }, [homeUsage.count, homeUsage.limit]);

    const isRemoteUpdateRef = useRef(false);
    const persistTimerRef = useRef(null);
    const legacyFoldersRef = useRef([]);
    const hasNonEmptyFoldersSnapshotRef = useRef(false);

    useEffect(() => {
        legacyFoldersRef.current = folders;
    }, [folders]);

    // helper: subscribe to folders in realtime (cards are loaded on-demand per mode)
    const subscribeToFoldersRealtime = async (userId) => {
        const db = await waitForDbAvailable(3000);
        if (!db || !userId) {
            console.error('[subscribeToFoldersRealtime] Cannot subscribe - db:', !!db, 'userId:', userId);
            return null;
        }
        const foldersRef = collection(db, 'users', userId, 'folders');
        const unsubFolders = onSnapshot(foldersRef, async (foldersSnap) => {
            const docCount = foldersSnap.docs.length;
            let nextFolders = foldersSnap.docs.map(d => {
                const raw = d.data() || {};
                const createdAt = (raw.createdAt && typeof raw.createdAt.toMillis === 'function')
                    ? raw.createdAt.toMillis()
                    : (typeof raw.createdAt === 'number' ? raw.createdAt : Date.now());
                const updatedAt = (raw.updatedAt && typeof raw.updatedAt.toMillis === 'function')
                    ? raw.updatedAt.toMillis()
                    : (typeof raw.updatedAt === 'number' ? raw.updatedAt : createdAt);
                return {
                    id: d.id,
                    name: typeof raw.name === 'string' && raw.name.trim() ? raw.name : 'Untitled',
                    color: raw.color || 'from-teal-500 to-emerald-600',
                    createdAt,
                    updatedAt,
                    cards: [],
                    cardCount: 0,
                    flashNotes: typeof raw.flashNotes === 'string' ? raw.flashNotes : ''
                };
            });
            if (docCount > 0) hasNonEmptyFoldersSnapshotRef.current = true;
            const localCount = Array.isArray(legacyFoldersRef.current) ? legacyFoldersRef.current.length : 0;
            if (docCount === 0 && localCount > 0) return;
            isRemoteUpdateRef.current = true;
            setFolders(nextFolders);
            cacheFolderList(nextFolders);
            // Fetch counts per folder and merge
            try {
                const db2 = getDb();
                if (db2 && userId) {
                    const counts = {};
                    await Promise.all(nextFolders.map(async (f) => {
                        try {
                            const cntSnap = await getCountFromServer(collection(db2, 'users', userId, 'folders', f.id, 'cards'));
                            counts[f.id] = (cntSnap?.data?.().count ?? cntSnap?.count ?? 0);
                        } catch (_) { counts[f.id] = 0; }
                    }));
                    setFolders(prev => prev.map(f => ({ ...f, cardCount: counts[f.id] != null ? counts[f.id] : f.cardCount })));
                }
            } catch (_) { }
        });
        return () => {
            try { unsubFolders && unsubFolders(); } catch (_) { }
        };
    };

    // On-demand card loader for a folder
    const fetchCardsForFolder = async (folderId) => {
        if (!navigator.onLine) return getCachedFolderCards(folderId);
        try {
            const db = getDb();
            const currentUid = getCurrentUid();
            if (!db || !currentUid || !folderId) return [];
            const cardsSnap = await getDocs(collection(db, 'users', currentUid, 'folders', folderId, 'cards'));
            const cards = cardsSnap.docs.map(cd => {
                const c = cd.data() || {};
                const createdAt = (c.createdAt && typeof c.createdAt.toMillis === 'function')
                    ? c.createdAt.toMillis()
                    : (typeof c.createdAt === 'number' ? c.createdAt : (c._clientCreatedAt || Date.now()));
                return {
                    id: cd.id,
                    question: typeof c.question === 'string' ? c.question : '',
                    answer: typeof c.answer === 'string' ? c.answer : '',
                    isFlagged: !!c.isFlagged,
                    lastViewed: c.lastViewed || null,
                    createdAt,
                    // Preserve the clinical Q-bank link so cards-source exams keep real rationales.
                    ...clinicalCardLinkFields(c)
                };
            });
            cacheFolderCards(folderId, cards);
            return cards;
        } catch (_) {
            return getCachedFolderCards(folderId);
        }
    };

    // When opening a folder preview, lazily load its cards so the list renders
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const id = previewFolder && previewFolder.id;
                if (!id) return;
                const needsLoad = !Array.isArray(previewFolder.cards) || previewFolder.cards.length === 0;
                if (!needsLoad) return;
                const cards = await fetchCardsForFolder(id);
                if (cancelled) return;
                setPreviewFolder(prev => (prev && prev.id === id) ? { ...prev, cards } : prev);
                setFolders(prev => prev.map(f => f.id === id ? { ...f, cards } : f));
            } catch (_) { }
        })();
        return () => { cancelled = true; };
    }, [previewFolder?.id]);

    // Load folders/sort: prefer Firestore realtime, fallback to localStorage
    useEffect(() => {
        let unsub = null;
        (async () => {
            try {
                let currentUid = getCurrentUid();

                if (!currentUid) {
                    // Don't sign in anonymously here — this effect re-runs on every transient
                    // uid change (e.g. mid sign-in/sign-out during a real login), and racing our
                    // own signInAnonymously() against an in-flight sign-in can hijack the session
                    // into a fresh guest identity. Anonymous bootstrap is owned centrally in
                    // main.jsx; just wait for whatever auth transition is already happening.
                    try { await waitForUserAvailable(3000); } catch (_) { }
                    currentUid = getCurrentUid();
                }

                if (!navigator.onLine) {
                    const cached = await getCachedFolderList();
                    if (cached.length) setFolders(cached);
                }

                if (currentUid) {
                    unsub = await subscribeToFoldersRealtime(currentUid);
                } else {
                    console.warn('[FOLDER LOAD] Still no currentUid available after sign-in attempt, cannot subscribe to folders');
                }
            } catch (err) {
                console.error('[FOLDER LOAD] Error:', err);
            };
            try {
                const s = localStorage.getItem('ff:sort');
                if (s) setSortBy(s);
            } catch (_) { }
        })();
        return () => {
            try { if (typeof unsub === 'function') unsub(); } catch (_) { }
        };
    }, [uid, getCurrentUid]);

    const sortedFolders = useMemo(() => {
        const list = [...folders].filter(f => f.name.toLowerCase().includes(searchQuery.toLowerCase()));
        list.sort((a, b) => {
            if (sortBy === 'name') return a.name.localeCompare(b.name);
            if (sortBy === 'updated') return (b.updatedAt || 0) - (a.updatedAt || 0);
            if (sortBy === 'created') return (b.createdAt || 0) - (a.createdAt || 0);
            return 0;
        });
        return list;
    }, [folders, sortBy, searchQuery]);

    // Client-side pagination for folders grid
    const PAGE_SIZE = 12;
    const [page, setPage] = useState(1);
    useEffect(() => { setPage(1); }, [searchQuery, sortBy]);
    const pagedFolders = useMemo(() => sortedFolders.slice(0, page * PAGE_SIZE), [sortedFolders, page]);
    const hasMoreFolders = pagedFolders.length < sortedFolders.length;
    const isLoadingFolders = !sortedFolders.length && statusMsg === 'Loading folders...';

    const [duplicatePrompt, setDuplicatePrompt] = useState(null);
    const [creditPrompt, setCreditPrompt] = useState(null);
    const [deletePrompt, setDeletePrompt] = useState(null);
    const handleCreate = (payload) => {
        const name = typeof payload === 'string' ? payload : (payload?.name || 'Untitled');
        const exists = folders.some(f => (f.name || '').trim().toLowerCase() === name.trim().toLowerCase());
        if (exists) { setDuplicatePrompt({ name }); return; }
        const color = typeof payload === 'string' ? 'from-teal-500 to-emerald-600' : (payload?.color || 'from-teal-500 to-emerald-600');
        const now = Date.now();
        const id = (crypto?.randomUUID && crypto.randomUUID()) || String(now + Math.random());
        setFolders(prev => {
            const next = [...prev, { id, name, color, createdAt: now, updatedAt: now, cards: [] }];
            legacyFoldersRef.current = next;
            return next;
        });
        // Persist directly to Firestore - get uid from state or auth instance
        try {
            (async () => {
                const db = await waitForDbAvailable(3000);
                if (!db) { console.warn('[FOLDER CREATE] Cannot save - db missing'); return; }

                let currentUid = getCurrentUid();
                if (!currentUid) {
                    try { await waitForUserAvailable(1500); } catch (_) { }
                    currentUid = getCurrentUid();
                }
                if (!currentUid) {
                    try {
                        const auth = getAuthInstance();
                        if (auth) {
                            await signInAnonymously(auth);
                            try { await waitForUserAvailable(2000); } catch (_) { }
                            currentUid = getCurrentUid();
                        }
                    } catch (e) {
                        console.error('[FOLDER CREATE] Error signing in anonymously:', e);
                    }
                }
                if (!currentUid) { console.warn('[FOLDER CREATE] Cannot save - currentUid missing'); return; }

                try {
                    await setDoc(doc(db, 'users', currentUid, 'folders', id), {
                        name,
                        color,
                        createdAt: serverTimestamp(),
                        updatedAt: serverTimestamp()
                    }, { merge: true });
                } catch (err) {
                    console.error('[FOLDER CREATE] Error writing to Firestore:', err);
                }
            })();
        } catch (err) {
            console.error('[FOLDER CREATE] Exception:', err);
        }
        setIsCreateOpen(false);
    };

    const handleRename = (id, name) => {
        const now = Date.now();
        setFolders(prev => prev.map(f => f.id === id ? { ...f, name, updatedAt: now } : f));
        try {
            const db = getDb();
            const currentUid = getCurrentUid();
            if (db && currentUid && id) {
                updateDoc(doc(db, 'users', currentUid, 'folders', id), {
                    name,
                    updatedAt: serverTimestamp()
                }).catch(() => { });
            }
        } catch (_) { }
        setRenameId(null);
        setMenuOpenId(null);
    };

    const handleDelete = (id) => {
        // Open confirmation prompt before deleting
        try {
            const f = folders.find(x => x.id === id);
            setDeletePrompt({ id, name: f?.name || 'this folder' });
        } catch (_) {
            setDeletePrompt({ id, name: 'this folder' });
        }
        setMenuOpenId(null);
    };

    const toggleMenu = (id) => {
        setMenuOpenId(curr => curr === id ? null : id);
    };
    const menuRef = useRef(null);
    useEffect(() => {
        const onClickAway = (e) => {
            if (!menuRef.current) return;
            if (!menuRef.current.contains(e.target)) setMenuOpenId(null);
        };
        if (menuOpenId) document.addEventListener('mousedown', onClickAway);
        return () => document.removeEventListener('mousedown', onClickAway);
    }, [menuOpenId]);

    const setSort = (value) => {
        setSortBy(value);
        setIsSortOpen(false);
        try { localStorage.setItem('ff:sort', value); } catch (_) { }
    };

    const handleImportCards = async ({ cards, folderId, folderName }) => {
        const db = getDb();
        const currentUid = getCurrentUid();
        if (!db || !currentUid) throw new Error('Not signed in');
        let targetId = folderId;
        if (!targetId && folderName) {
            const newFolderRef = doc(collection(db, 'users', currentUid, 'folders'));
            targetId = newFolderRef.id;
            await setDoc(newFolderRef, { name: folderName, createdAt: serverTimestamp(), updatedAt: serverTimestamp(), cardCount: 0 });
        }
        if (!targetId) throw new Error('No target folder specified');
        await Promise.all(cards.map(async ({ question, answer }) => {
            const cid = doc(collection(db, 'users', currentUid, 'folders', targetId, 'cards')).id;
            await setDoc(doc(db, 'users', currentUid, 'folders', targetId, 'cards', cid), {
                question, answer, confidence: 'again', isFlagged: false,
                createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
            });
        }));
        const folderRef = doc(db, 'users', currentUid, 'folders', targetId);
        await updateDoc(folderRef, { cardCount: cards.length, updatedAt: serverTimestamp() }).catch(() => {});
    };

    const handleExportBackup = async () => {
        setIsExportingBackup(true);
        try {
            // Backup needs every card in every folder, not just whichever ones the user
            // has already previewed this session — hydrate anything still empty.
            const hydratedFolders = await Promise.all(folders.map(async (f) => {
                if (Array.isArray(f.cards) && f.cards.length > 0) return f;
                const cards = await fetchCardsForFolder(f.id);
                return { ...f, cards };
            }));
            await exportBackupZip({ folders: hydratedFolders, savedExams });
            pushToast('success', 'Backup exported');
        } catch (e) {
            pushToast('error', e?.message || 'Backup export failed');
        } finally {
            setIsExportingBackup(false);
        }
    };

    const handleRestoreBackup = async ({ manifest, zip, selectedFolderIds }) => {
        const db = getDb();
        const currentUid = getCurrentUid();
        if (!db || !currentUid) throw new Error('Not signed in');

        const existingNames = new Set(folders.map((f) => (f.name || '').trim().toLowerCase()));

        for (const folder of manifest.folders || []) {
            if (!selectedFolderIds.has(folder.id)) continue;

            let name = folder.name || 'Restored Deck';
            if (existingNames.has(name.trim().toLowerCase())) name = `${name} (Restored)`;
            existingNames.add(name.trim().toLowerCase());

            const newFolderRef = doc(collection(db, 'users', currentUid, 'folders'));
            await setDoc(newFolderRef, {
                name,
                color: folder.color || 'from-teal-500 to-emerald-600',
                flashNotes: folder.flashNotes || '',
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
                cardCount: 0,
            });

            // Collapse any duplicate cards within the backup itself (not a merge check
            // against the user's live library — restore always creates new folders).
            const { unique: uniqueCards } = partitionUniqueCards(folder.cards || [], new Set());

            await Promise.all(uniqueCards.map(async (card) => {
                let imageUrl = null;
                if (card.imageFileName) {
                    try {
                        const blob = await getBackupImageBlob(zip, card.imageFileName);
                        if (blob) {
                            const file = new File([blob], card.imageFileName, { type: blob.type || 'image/png' });
                            imageUrl = await uploadCardImage(currentUid, file);
                        }
                    } catch (_) { /* image restore is best-effort; card still restores without it */ }
                }
                const cid = doc(collection(db, 'users', currentUid, 'folders', newFolderRef.id, 'cards')).id;
                await setDoc(doc(db, 'users', currentUid, 'folders', newFolderRef.id, 'cards', cid), {
                    question: card.question ?? '',
                    answer: card.answer ?? '',
                    isFlagged: !!card.isFlagged,
                    confidence: card.confidence ?? 'again',
                    clinicalQuestionId: card.clinicalQuestionId ?? null,
                    imageUrl,
                    createdAt: typeof card.createdAt === 'number' ? card.createdAt : serverTimestamp(),
                    updatedAt: serverTimestamp(),
                });
            }));

            await updateDoc(newFolderRef, { cardCount: uniqueCards.length, updatedAt: serverTimestamp() }).catch(() => {});
        }

        pushToast('success', 'Backup restored');
    };

    const handleQuickTest = (folder) => {
        // Preselect just this folder and open the exam creation modal
        setExamPreSelectedFolderId(folder.id);
        setShowExamCreate(true);
    };

    const renderAppHomeFoldersSection = (options = {}) => (
        <AppHomeFoldersSection
            sectionClassName={options.sectionClassName ?? 'lg:max-w-6xl lg:mx-auto'}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            sortBy={sortBy}
            isSortOpen={isSortOpen}
            onSortOpenToggle={setIsSortOpen}
            onSetSort={setSort}
            sortedFolders={sortedFolders}
            pagedFolders={pagedFolders}
            onCreateFolder={() => setIsCreateOpen(true)}
            onPreviewFolder={setPreviewFolder}
            onDeleteFolder={handleDelete}
            hasMoreFolders={options.hasMoreFolders ?? false}
            onLoadMore={options.onLoadMore}
            showLoadingSkeleton={options.showLoadingSkeleton ?? false}
            isLoadingFolders={options.showLoadingSkeleton ? !uid || !folders.length : false}
            onShareFolder={setShareFolderTarget}
            onQuickTest={handleQuickTest}
            onImportCards={() => setShowImportModal(true)}
            onExportBackup={handleExportBackup}
            isExportingBackup={isExportingBackup}
            onRestoreBackup={() => setShowRestoreBackupModal(true)}
            isOnline={isOnline}
        />
    );

    // --- Folder actions wiring (Study, Games, Notes, Exam, Exports) ---
    const openStudy = async (folder) => {
        try { lastPreviewFolderRef.current = folder; } catch (_) { }
        // On-demand load cards to ensure viewer mounts fast with data
        try { await import('../components/FlashcardViewer.jsx'); } catch (_) { }
        const cards = await fetchCardsForFolder(folder.id);
        setStudyingFolder({ ...folder, cards });
    };
    const handlePlayGame = async (folder) => {
        setMostRecentScore(null);
        // Stage folder with on-demand cards for faster start
        try { await import('../components/GameViewer.jsx'); } catch (_) { }
        const cards = await fetchCardsForFolder(folder.id);
        setShowGamesModal({ ...folder, cards });
    };
    const handleLaunchGame = async (folder) => {
        try {
            if (!isDevMode) {
                await authApi(`${API_BASE_URL}/api/credits/debit`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ feature: 'game', quantity: 1 }) });
            }
            if (typeof refreshEntitlements === 'function') { try { await refreshEntitlements(); } catch (_) { } }
            setShowGamesModal(null);
            setGameModeFolder(folder);
        } catch (e) {
            console.error('[AppHome] game debit failed:', { code: e?.code, requestId: e?.requestId, status: e?.status });
            if (e?.code === 'INSUFFICIENT_CREDITS') {
                pushToast('error', e.message || 'Not enough credits');
            } else {
                pushToast('error', e?.message || 'Failed to start game');
            }
        }
    };
    const handleLaunchNemesis = async (folder) => {
        try {
            if (!isDevMode) {
                await authApi(`${API_BASE_URL}/api/credits/debit`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ feature: 'game', quantity: 1 }) });
            }
            if (typeof refreshEntitlements === 'function') { try { await refreshEntitlements(); } catch (_) { } }
            setNemesisModeFolder(folder);
        } catch (e) {
            console.error('[AppHome] nemesis debit failed:', { code: e?.code, requestId: e?.requestId, status: e?.status });
            if (e?.code === 'INSUFFICIENT_CREDITS') {
                setCreditPrompt({ type: 'games', folder });
            } else {
                pushToast('error', e?.message || 'Failed to start Nemesis game');
            }
        }
    };
    const handleGameEnd = (folderId, finalScore, playerName, levelName) => {
        const newScoreEntry = { id: Date.now(), name: playerName, score: finalScore, date: Date.now(), level: levelName };
        setFolders(prev => prev.map(f => f.id === folderId ? { ...f, leaderboard: [...(f.leaderboard || []), newScoreEntry].sort((a, b) => b.score - a.score || b.date - a.date).slice(0, 10) } : f));
        setMostRecentScore(newScoreEntry);
        pushToast('success', `Game finished: ${finalScore} pts`);
    };
    const [flashNotesAction, setFlashNotesAction] = useState(null);
    const lastPreviewFolderRef = useRef(null);
    const handleGenerateNotes = async (folder, action, forceRegenerate = false, prepaid = false, organizationStyle = 'auto') => {
        // Keep the action modal visible during generation so spinner is shown
        // remember which folder was open so we can restore after viewing notes
        try { lastPreviewFolderRef.current = folder || previewFolder || null; } catch (_) { }
        if (folder.flashNotes && !forceRegenerate) {
            if (action === 'view') {
                try { await import('../components/FlashNotesViewer.jsx'); } catch (_) { }
                setFlashNotesContent({ folderId: folder.id, folderName: folder.name, notes: folder.flashNotes });
                setShowFlashNotesViewer(true);
            } else if (action === 'export') {
                try {
                    const [{ default: jsPDF }, { marked }] = await Promise.all([import('jspdf'), import('marked')]);
                    exportNotesToPDF(folder.name, folder.flashNotes, jsPDF, marked);
                } catch (e) { pushToast('error', 'Export failed'); }
            }
            return;
        }
        // UX pre-check: show credit modal early if client knows there are no credits
        // (server enforces this too; this just avoids an unnecessary spinner)
        try {
            const includedLeft = Number(entitlements?.includedNotesRemaining ?? 0);
            const creditsLeft = Number(entitlements?.creditsBalance ?? 0);
            if (!isDevMode && includedLeft <= 0 && creditsLeft <= 0 && !prepaid) {
                setFlashNotesAction(null);
                setCreditPrompt({ type: 'notes', folder, action, forceRegenerate });
                return;
            }
        } catch (_) { }
        setIsGeneratingNotes(true);
        setStatusMsg('Synthesizing your Flash Notes...');
        try {
            const data = await authApiIdempotent(`${API_BASE_URL}/generate-notes`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ cards: folder.cards, organizationStyle }) });
            const notes = data.notes;
            setFolders(prev => prev.map(f => f.id === folder.id ? { ...f, flashNotes: notes } : f));
            // Persist notes to Firestore on the folder for cross-device retrieval
            try {
                const db = getDb();
                const currentUid = getCurrentUid();
                if (db && currentUid) {
                    await updateDoc(doc(db, 'users', currentUid, 'folders', folder.id), { flashNotes: notes, updatedAt: serverTimestamp() });
                }
            } catch (_) { }
            if (action === 'view') {
                try { await import('../components/FlashNotesViewer.jsx'); } catch (_) { }
                setFlashNotesContent({ folderId: folder.id, folderName: folder.name, notes });
                setShowFlashNotesViewer(true);
            } else if (action === 'export') {
                try {
                    const [{ default: jsPDF }, { marked }] = await Promise.all([import('jspdf'), import('marked')]);
                    exportNotesToPDF(folder.name, notes, jsPDF, marked);
                } catch { }
            }
            if (typeof refreshEntitlements === 'function') { try { await refreshEntitlements(); } catch (_) { } }
            try { trackEvent(Events.notes_generate, { folderId: folder.id, organizationStyle }); } catch (_) { }
            pushToast('success', 'Flash Notes generated');
        } catch (e) {
            console.error('[AppHome] generate notes failed:', { code: e?.code, requestId: e?.requestId, status: e?.status });
            if (e?.code === 'INSUFFICIENT_CREDITS') {
                setFlashNotesAction(null);
                setCreditPrompt({ type: 'notes', folder, action, forceRegenerate });
            } else {
                setStatusMsg(e.message);
                pushToast('error', e.message || 'Generate notes failed');
            }
        } finally {
            setIsGeneratingNotes(false);
            setFlashNotesAction(null);
        }
    };
    const exportFolderToPDF = async (folder) => {
        const input = window.prompt('How many flashcards per page? (6, 8, or 10)', '8');
        const n = parseInt(input || '8', 10);
        if (![6, 8, 10].includes(n)) { pushToast('error', 'Invalid number. Choose 6, 8, or 10.'); return; }
        try {
            const { default: jsPDF } = await import('jspdf');
            const doc = new jsPDF();
            await buildFolderPdf(doc, folder, n, renderContentForPdf);
            doc.save(`${folder.name}-flashcards.pdf`);
            try { trackEvent(Events.export_pdf, { folderId: folder.id, cardCount: (folder.cards || []).length }); } catch (_) { }
            pushToast('success', 'PDF generated');
        } catch (e) { pushToast('error', 'Export PDF failed'); }
    };
    const exportFolderToCSV = (folder) => {
        try {
            const cards = folder.cards || [];
            let csv = 'FlashFonic\nListen. Flash it. Learn.\n\n';
            csv += 'Question,Answer\n';
            cards.forEach(c => {
                const q = (c.question || '').toString().replace(/"/g, '""');
                const a = JSON.stringify(c.answer ?? '').replace(/"/g, '""');
                csv += `"${q}","${a}"\n`;
            });
            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = `${folder.name}-flashcards.csv`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(link.href);
            try { trackEvent(Events.export_csv, { folderId: folder.id, cardCount: cards.length }); } catch (_) { }
            pushToast('success', 'CSV exported');
        } catch (_) { pushToast('error', 'Export CSV failed'); }
    };
    const [isExportingAnki, setIsExportingAnki] = useState(false);
    const exportFolderToAnkiFile = async (folder) => {
        setIsExportingAnki(true);
        try {
            await exportFolderToAnki(folder);
            try { trackEvent(Events.export_anki, { folderId: folder.id, cardCount: (folder.cards || []).length }); } catch (_) { }
            pushToast('success', 'Anki deck exported');
        } catch (e) {
            pushToast('error', e.message || 'Export Anki failed');
        } finally {
            setIsExportingAnki(false);
        }
    };
    // --- Exam hub wiring ---
    const handleStartExam = () => setShowExamCreate(true);
    const handleCreateExam = (config) => {
        setShowExamCreate(false);
        setExamWizardState(null);
        setExamPreSelectedFolderId(null);

        // Non-blocking: close modal immediately, show queue pill, study freely while it builds.
        setExamQueue({ status: 'creating', exam: null });

        (async () => {
            try {
                const selectedIds = config.selectedFolderIds
                    ? Object.keys(config.selectedFolderIds).filter(id => config.selectedFolderIds[id])
                    : Object.keys(examSelectedFolderIds).filter(id => examSelectedFolderIds[id]);
                if (selectedIds.length === 0) { setExamQueue(null); return; }

                const selected = folders.filter(f => selectedIds.includes(f.id));
                const resolved = await Promise.all(selected.map(async (f) => {
                    if (Array.isArray(f.cards) && f.cards.length > 0) return f;
                    const cards = await fetchCardsForFolder(f.id);
                    setFolders(prev => prev.map(x => x.id === f.id ? { ...x, cards } : x));
                    return { ...f, cards };
                }));
                let allCards = resolved.flatMap(f => f.cards || []);
                if (config.flaggedOnly) {
                    allCards = allCards.filter(c => c.isFlagged);
                }
                if (!allCards.length) {
                    setExamQueue(null);
                    pushToast('info', config.flaggedOnly ? 'No flagged cards in selected folders' : 'No cards in selected folders');
                    return;
                }

                let exam;
                if (productMode === 'clinical') {
                    const sourceText = allCards
                        .map((c, i) => `[Concept ${i + 1}]\n${c.question}\n${c.answer}`)
                        .join('\n\n---\n\n');
                    const clinicalConfig = { ...config, questionCount: Math.min(config.questionCount || 10, allCards.length) };
                    exam = await createClinical(sourceText, buildClinicalPayload(), clinicalConfig);
                } else {
                    exam = await createExamFromCards(allCards, config);
                }

                if (!exam) { setExamQueue(null); return; }
                setSavedExams(prev => [exam, ...prev]);
                setExamQueue({ status: 'ready', exam });
                // Auto-save immediately on creation, not only after the exam is finished —
                // previously an exam that was generated but closed out of before completion
                // (e.g. no time to take it right now) was never written to savedExams at
                // all, even though the questions already existed server-side. Uses the
                // backend-generated title (topic-based, or "FlashFonic Exam (date)") as the
                // default name; finishing the exam later still updates this same doc with
                // the score via the existing pendingExamSave/EnterNameModal flow (merge).
                try {
                    const db = getDb();
                    const currentUid = getCurrentUid();
                    const examId = exam.examId || exam.id;
                    if (db && currentUid && examId) {
                        const docRef = doc(db, 'users', currentUid, 'savedExams', String(examId));
                        setDoc(docRef, {
                            title: exam.title || `FlashFonic Exam (${new Date().toLocaleDateString()})`,
                            examId,
                            config: exam.config || null,
                            questions: exam.questions || [],
                            createdAt: serverTimestamp(),
                        }, { merge: true });
                    }
                } catch (_) { }
                try { trackEvent(Events.exam_created, { questionCount: allCards.length, folderCount: selectedIds.length, flaggedOnly: !!config.flaggedOnly, mode: productMode }); } catch (_) { }
            } catch (e) {
                setExamQueue(null);
                pushToast('error', e.message || 'Failed to create exam');
            } finally {
                setExamSelectedFolderIds({});
            }
        })();
    };
    // Retake = open the viewer for the saved exam; it starts a fresh server attempt
    // (server-fixed shuffle) on mount. No client-side scramble.
    const handleRetakeExam = (examId) => {
        const ex = savedExams.find(e => e.id === examId);
        if (!ex) return;
        try { trackEvent(Events.exam_retake, { examId }); } catch (_) { }
        setActiveExam(ex);
        setExamWizardState(null);
    };
    const handleSaveAndRecordExam = (examData, finalScore) => {
        const totalQuestions = Array.isArray(examData?.questions) ? examData.questions.length : 0;
        const percentage = totalQuestions ? Math.round((finalScore / totalQuestions) * 100) : 0;
        addXP(XP_PER_EXAM);
        recordStudyToday();
        setPendingExamSave({ exam: examData, percentage, score: finalScore, totalQuestions });
    };
    // `items` come from the server answer key: [{ questionText, sourceCardIds, answerText }].
    // Prefer the original flashcards via sourceCardIds; clinical questions have none, so we
    // synthesize a card from the question + correct option text.
    const handleCreateFlaggedFolder = async (items = []) => {
        try {
            if (!Array.isArray(items) || !items.length) { pushToast('info', 'No questions flagged'); return; }

            const sourceIds = new Set();
            for (const it of items) (it?.sourceCardIds || []).forEach(id => sourceIds.add(String(id)));
            const allCards = folders.flatMap(f => f.cards || []);
            const fromSource = sourceIds.size ? allCards.filter(c => sourceIds.has(String(c.id))) : [];
            let derivedCards = fromSource.length
                ? fromSource
                : items.map(it => ({ id: String(Date.now() + Math.random()), question: it.questionText, answer: it.answerText || '', isFlagged: false, lastViewed: null }));

            if (!derivedCards.length) { pushToast('info', 'No source cards found for flagged questions'); return; }
            const { unique: uniqueDerivedCards, duplicates: dropped } = partitionUniqueCards(derivedCards, new Set());
            derivedCards = uniqueDerivedCards;

            const now = Date.now();
            const newFolderId = (crypto?.randomUUID && crypto.randomUUID()) || String(now + Math.random());
            const name = `Flagged ${new Date(now).toLocaleDateString()}`;
            const color = 'from-teal-600 to-emerald-600';

            // Optimistic UI
            setFolders(prev => [...prev, { id: newFolderId, name, color, createdAt: now, updatedAt: now, cards: derivedCards }]);

            // Persist to Firestore
            try {
                const db = getDb();
                const currentUid = getCurrentUid();
                if (!db || !currentUid) return;
                await setDoc(doc(db, 'users', currentUid, 'folders', newFolderId), {
                    name,
                    color,
                    createdAt: serverTimestamp(),
                    updatedAt: serverTimestamp()
                }, { merge: true });
                for (const c of derivedCards) {
                    const cid = String(c.id || `${Date.now()}.${Math.floor(Math.random() * 10000)}`);
                    await setDoc(doc(db, 'users', currentUid, 'folders', newFolderId, 'cards', cid), {
                        question: typeof c.question === 'string' ? c.question : '',
                        answer: typeof c.answer === 'string' ? c.answer : '',
                        isFlagged: !!c.isFlagged,
                        lastViewed: c.lastViewed || null,
                        createdAt: serverTimestamp(),
                        ...clinicalCardLinkFields(c)
                    });
                }
            } catch (_) { }

            pushToast('success', `Created folder with ${derivedCards.length} cards${dropped ? `, skipped ${dropped} duplicate${dropped === 1 ? '' : 's'}` : ''}`);
        } catch (_) { pushToast('error', 'Failed to create folder from flagged'); }
    };

    // Auto-flag the source card behind a wrong exam answer, in place, in its existing folder —
    // so it's immediately part of the "flagged cards only" exam pool without a manual step.
    const handleIncorrectAnswers = async (cardIds = []) => {
        if (!Array.isArray(cardIds) || !cardIds.length) return;
        const wanted = new Set(cardIds.map(String));
        const db = getDb();
        const currentUid = getCurrentUid();
        if (!db || !currentUid) return;

        let flaggedCount = 0;
        const updatedFolderCards = new Map(); // folderId -> updated cards array

        for (const folder of folders) {
            const cards = folder.cards || [];
            let changed = false;
            const nextCards = cards.map((c) => {
                if (!wanted.has(String(c.id)) || c.isFlagged) return c;
                changed = true;
                flaggedCount += 1;
                updateDoc(doc(db, 'users', currentUid, 'folders', folder.id, 'cards', String(c.id)), { isFlagged: true }).catch(() => {});
                return { ...c, isFlagged: true };
            });
            if (changed) updatedFolderCards.set(folder.id, nextCards);
        }

        if (updatedFolderCards.size === 0) return;
        setFolders((prev) => prev.map((f) => (updatedFolderCards.has(f.id) ? { ...f, cards: updatedFolderCards.get(f.id) } : f)));
        if (flaggedCount > 0) {
            pushToast('info', `${flaggedCount} card${flaggedCount === 1 ? '' : 's'} flagged based on this exam — ready for a flagged-only review`);
        }
    };

    // "View source flashcard" from an exam's wrong-answer breakdown — ExamViewer has no
    // Firestore/folder access itself, so it just hands back sourceCardIds and this page
    // resolves them against the folders it already has loaded.
    const [viewingSourceCards, setViewingSourceCards] = useState(null);
    const handleViewSourceCard = (cardIds = []) => {
        const wanted = new Set(cardIds.map(String));
        const found = folders.flatMap((f) => (f.cards || [])
            .filter((c) => wanted.has(String(c.id)))
            .map((c) => ({ ...c, __folderId: f.id })));
        if (!found.length) {
            pushToast('info', 'Could not find the original flashcard — it may have been edited or removed');
            return;
        }
        setViewingSourceCards(found);
    };

    // "View source document" from within the original-flashcard viewer above — opens the
    // exact uploaded document (jumped to the page it was highlighted from) the same way
    // DeckPage's "Documents" menu reopens one, just entered from a card instead of the menu.
    const handleViewSourceDocument = async (card) => {
        try {
            const db = getDb();
            const uid = getCurrentUid();
            if (!db || !uid || !card?.sourceDocumentId || !card?.__folderId) return;
            const snap = await getDoc(doc(db, 'users', uid, 'folders', card.__folderId, 'sourceDocuments', card.sourceDocumentId));
            if (!snap.exists()) {
                pushToast('info', 'That source document is no longer available');
                return;
            }
            setDocumentStudyFile(null);
            setStudyDocument({ id: snap.id, ...snap.data() });
            setDocumentStudyInitialPage(card.sourceDocumentPage || null);
            setTargetFolderId(card.__folderId);
            setShowDocumentStudyModal(true);
            setViewingSourceCards(null);
        } catch (_) {
            pushToast('info', 'Could not open the source document');
        }
    };

    // --- FlashFoto handlers ---
    const openFilePicker = () => {
        fileInputRef.current?.click();
    };

    const isPdfFile = (file) => file?.type === 'application/pdf';

    const handleIncomingFile = (file) => {
        if (!file) return;
        // PDFs open the full-document study modal directly instead of going through the
        // inline uploader/analyzeDocument() at all — generation happens per-highlighted
        // selection inside that modal, not via the single whole-document "Generate" button.
        if (isPdfFile(file)) {
            setImagePreview(null);
            setDocumentFileName(null);
            setAiAnalysis(null);
            setDocumentStudyFile(file);
            setShowDocumentStudyModal(true);
            return;
        }
        // Word/PPT/Excel: convert to PDF server-side first (LibreOffice), then open the same
        // study modal — same visual, scroll-and-highlight experience PDFs already get.
        if (isOfficeFile(file)) {
            setImagePreview(null);
            setDocumentFileName(null);
            setAiAnalysis(null);
            setIsGenerating(true);
            setStatusMsg('Converting document...');
            convertOfficeFileToPdf(file)
                .then((pdfFile) => {
                    setIsGenerating(false);
                    setStatusMsg('');
                    setDocumentStudyFile(pdfFile);
                    setShowDocumentStudyModal(true);
                })
                .catch(() => {
                    // Visual conversion isn't set up yet (or this file failed to convert) —
                    // fall back to the older whole-document text-extraction flow so Office
                    // uploads never regress to a hard error while the converter service is
                    // being stood up.
                    setDocumentFileName(file.name);
                    const reader = new FileReader();
                    reader.onloadend = () => {
                        const dataUrl = reader.result;
                        let base64 = '';
                        try { base64 = dataUrl.split(',')[1]; } catch (_) {}
                        lastBase64Ref.current = base64;
                        analyzeDocument(base64, file.type);
                    };
                    reader.readAsDataURL(file);
                });
            return;
        }
        setDocumentFileName(null);
        const reader = new FileReader();
        reader.onloadend = () => {
            const dataUrl = reader.result;
            setImagePreview(dataUrl);
            try { lastBase64Ref.current = dataUrl.split(',')[1]; } catch (_) { }
            analyzeImage(lastBase64Ref.current);
        };
        reader.readAsDataURL(file);
    };

    const handleFileChange = (e) => {
        const file = e.target.files?.[0];
        handleIncomingFile(file);
    };

    const onDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        const file = e.dataTransfer.files?.[0];
        handleIncomingFile(file);
    };

    const onSelectionError = (message) => {
        pushToast('error', message || 'Selection failed');
    };

    // Re-runs the existing image-analysis path with a cropped region instead of the full
    // photo — same downstream flow (analyzeImage → aiAnalysis.extractedText), just a smaller,
    // more targeted image feeding the OCR call.
    const onImageCropReady = (croppedDataUrl) => {
        setImagePreview(croppedDataUrl);
        let base64 = '';
        try { base64 = croppedDataUrl.split(',')[1]; } catch (_) {}
        lastBase64Ref.current = base64;
        analyzeImage(base64);
    };

    const analyzeDocument = async (base64, mimeType) => {
        if (!base64) return;
        setIsGenerating(true);
        setStatusMsg('Extracting document text...');
        pushToast('info', 'Extracting document text...');
        setAiAnalysis(null);
        try {
            const data = await authApiIdempotent(`${API_BASE_URL}/analyze-document`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ document_data: base64, mime_type: mimeType })
            });
            const rec = Math.max(1, Math.min(20, Number(data.recommendation || 0)));
            setAiAnalysis({ recommendation: rec, extractedText: data.extractedText });
            setStatusMsg('Extraction complete. Ready to generate.');
            pushToast('success', 'Document text extracted');
        } catch (e) {
            console.error('[AppHome] analyze-document failed:', { code: e?.code, requestId: e?.requestId, status: e?.status });
            setStatusMsg(e.message);
            pushToast('error', e.message || 'Document extraction failed');
        } finally {
            setIsGenerating(false);
        }
    };

    const analyzeImage = async (base64) => {
        if (!base64) return;
        setIsGenerating(true);
        setStatusMsg('Analyzing image...');
        pushToast('info', 'Analyzing image...');
        setAiAnalysis(null);
        try {
            const data = await authApiIdempotent(`${API_BASE_URL}/analyze-image`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ image_data: base64 })
            });
            const rec = Math.max(1, Math.min(20, Number(data.recommendation || 0)));
            setAiAnalysis({ recommendation: rec, extractedText: data.extractedText });
            setStatusMsg('Analysis complete. Ready to generate.');
            pushToast('success', 'Analysis complete');
        } catch (e) {
            console.error('[AppHome] analyze-image failed:', { code: e?.code, requestId: e?.requestId, status: e?.status });
            setStatusMsg(e.message);
            pushToast('error', e.message || 'Analyze failed');
        } finally {
            setIsGenerating(false);
        }
    };

    const handleAutoGenerate = async (overrideCount) => {
        if (!aiAnalysis && lastBase64Ref.current) {
            await analyzeImage(lastBase64Ref.current);
        }
        if (!aiAnalysis?.extractedText) return;
        // If no override provided, show AI recommendation modal to confirm
        if (typeof overrideCount !== 'number') {
            const rec = Math.max(1, Math.min(20, Number(aiAnalysis?.recommendation || cardCount)));
            setFotoAIRec(rec);
            setShowFotoAIModal(true);
            return;
        }
        const chosenCount = overrideCount;
        setIsGenerating(true);
        setStatusMsg('Generating flashcards...');
        try {
            try { trackEvent(Events.generation_foto, { count: chosenCount }); } catch { }
            // Credits (1 per card) are debited atomically server-side inside
            // /generate-flashcards-from-image and refunded in full if generation fails —
            // no client-side pre-debit anymore.
            const data = await authApiIdempotent(`${API_BASE_URL}/generate-flashcards-from-image`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ text: aiAnalysis.extractedText, cardCount: chosenCount })
            });
            if (typeof refreshEntitlements === 'function') { try { await refreshEntitlements(); } catch (_) { } }
            const cards = (data.cards || []).map((c) => ({ id: String(Date.now() + Math.random()), question: c.question || c.q || c.prompt || '', answer: c.answer || c.a || '', isFlagged: false, lastViewed: null }));
            // Do not auto-create or auto-append to a folder. Keep in Review Queue and let the user choose where to move.
            setStatusMsg(`${cards.length} flashcards generated!`);
            pushToast('success', `${cards.length} flashcards generated`);
            bumpHomeUsage(cards.length || 1);
            // Push generated cards into the unified Review Queue (avoiding exact duplicates)
            setHomeQueued(prev => {
                const existing = new Set(prev.map(c => `${(c.question || '').trim()}|${(c.answer || '').trim()}`));
                const uniqueNew = cards.filter(c => !existing.has(`${(c.question || '').trim()}|${(c.answer || '').trim()}`));
                return [...uniqueNew, ...prev];
            });
            // Dispatch global event so promo funnel can trigger based on thresholds
            try { window.dispatchEvent(new CustomEvent('ff:cardsGenerated', { detail: { increment: Number(cards.length || 1) } })); } catch (_) {}
            setImagePreview(null); setAiAnalysis(null); lastBase64Ref.current = null;
        } catch (e) {
            console.error('[AppHome] foto generation failed:', { code: e?.code, requestId: e?.requestId, status: e?.status });
            if (e?.code === 'INSUFFICIENT_CREDITS') {
                const required = e?.details?.required;
                const available = e?.details?.available;
                setStatusMsg(required != null && available != null ? `Not enough credits. Need ${required}, have ${available}.` : (e?.message || 'Not enough credits.'));
                pushToast('error', 'Not enough credits');
            } else {
                setStatusMsg(e.message);
                pushToast('error', e.message || 'Generation failed');
            }
        } finally {
            setIsGenerating(false);
        }
    };

    const buildClinicalPayload = (settings = clinicalSettings) => ({
        examType: settings.examType,
        cardStyle: settings.cardStyle,
    });

    const appendCardsToReviewQueue = (cards) => {
        setHomeQueued((prev) => {
            const existing = new Set(prev.map((c) => `${(c.question || '').trim()}|${(c.answer || '').trim()}`));
            const uniqueNew = cards.filter((c) => !existing.has(`${(c.question || '').trim()}|${(c.answer || '').trim()}`));
            return [...uniqueNew, ...prev];
        });
        try {
            window.dispatchEvent(new CustomEvent('ff:cardsGenerated', { detail: { increment: Number(cards.length || 1) } }));
        } catch (_) {}
    };

    const handleClinicalBatchGenerate = async () => {
        let sourceText = clinicalNotes.trim();
        if (clinicalInputType === 'image') {
            if (!lastBase64Ref.current && imagePreview) {
                try {
                    lastBase64Ref.current = imagePreview.split(',')[1];
                } catch (_) {}
            }
            if (!aiAnalysis?.extractedText && lastBase64Ref.current) {
                await analyzeImage(lastBase64Ref.current);
            }
            sourceText = (aiAnalysis?.extractedText || '').trim();
        }
        if (!sourceText) {
            pushToast('error', clinicalInputType === 'image' ? 'Upload a file and wait for analysis first' : 'Paste clinical content to generate cards');
            return;
        }
        const creditsOk =
            isDevMode ||
            (typeof entitlements?.creditsBalance === 'number' ? entitlements.creditsBalance > 0 : remaining > 0);
        if (!creditsOk) {
            pushToast('error', 'Not enough credits');
            return;
        }
        const tierLimits = CLINICAL_TIER_LIMITS[entitlements?.tier] || CLINICAL_TIER_LIMITS.free;
        const tierMax = tierLimits.maxCards; // backend clamps to the same 15 regardless of dev mode
        // A pasted image attaches to exactly one card — generating a batch and stamping the
        // same picture on several unrelated questions wouldn't make sense, so an attached
        // image forces a single-card request regardless of the normal 1-15 count picker.
        const chosenCount = clinicalPastedImage ? 1 : Math.max(1, Math.min(tierMax, Number(clinicalSettings.cardCount || 6)));
        setIsClinicalGenerating(true);
        setStatusMsg('Generating clinical flashcards...');
        try {
            // Upload the pasted image (if any) before spending credits on generation, so a
            // failed upload doesn't burn a generation call for nothing. The image is never
            // sent to or analyzed by the AI — purely a passenger that attaches to the
            // resulting card once generation succeeds.
            let uploadedImageUrl = null;
            if (clinicalPastedImage) {
                try {
                    uploadedImageUrl = await uploadCardImage(uid, clinicalPastedImage);
                } catch (err) {
                    pushToast('error', 'Image upload failed — try again');
                    setIsClinicalGenerating(false);
                    setStatusMsg('');
                    return;
                }
            }
            // Sending `clinical` routes this through the exam-type-aware clinical engine
            // (accuracy checking, citation validation) and its tiered per-exam-type credit
            // cost, debited atomically server-side with a refund on failure — previously
            // this omitted `clinical`, so it silently fell through to generic flashcard
            // generation billed at a flat 1 credit/card regardless of exam-type complexity.
            const data = await authApiIdempotent(`${API_BASE_URL}/generate-flashcards-from-image`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    text: sourceText,
                    cardCount: chosenCount,
                    clinical: buildClinicalPayload(),
                }),
            });
            if (typeof refreshEntitlements === 'function') { try { await refreshEntitlements(); } catch (_) {} }
            const cards = (data.cards || []).map((c) =>
                enrichClinicalCard(
                    {
                        id: String(Date.now() + Math.random()),
                        question: c.question || c.q || c.prompt || '',
                        answer: c.answer || c.a || '',
                        isFlagged: false,
                        lastViewed: null,
                        imageUrl: uploadedImageUrl,
                    },
                    clinicalSettings,
                    c
                )
            );
            appendCardsToReviewQueue(cards);
            bumpHomeUsage(cards.length || 1);
            try {
                trackEvent(Events.generation_foto, { count: chosenCount, mode: 'clinical' });
            } catch (_) {}
            setStatusMsg(`${cards.length} clinical flashcards generated`);
            pushToast('success', `${cards.length} clinical flashcards generated`);
            incrementFreeQuestionsUsed(cards.length);
            const nowUsed = getFreeQuestionsUsed();
            setFreeQuestionsUsed(nowUsed);
            const hasPaidTierNow = !!(entitlements?.tier && entitlements.tier !== 'free');
            if (nowUsed >= FREE_TRIAL_TOTAL && !hasPaidTierNow) setShowClinicalGate(true);
            if (trackGenerationForPromo()) {
                setPromoVariant('enjoying');
                setShowPromoModal(true);
            }
            if (clinicalInputType === 'image') {
                setImagePreview(null);
                setDocumentFileName(null);
                setAiAnalysis(null);
                lastBase64Ref.current = null;
            } else {
                setClinicalNotes('');
                clearClinicalPastedImage();
            }
        } catch (e) {
            setStatusMsg(e.message);
            pushToast('error', e.message || 'Clinical generation failed');
        } finally {
            setIsClinicalGenerating(false);
        }
    };

    // Fires after each in-modal generation inside ClinicalDocumentStudyModal (one call per
    // highlighted selection, potentially several per session) — credits are spent
    // server-side the moment that call succeeds, so usage bookkeeping needs to match here
    // exactly like it does in handleClinicalBatchGenerate, not wait until the modal closes.
    const handleDocumentModalCardsGenerated = async (count) => {
        if (typeof refreshEntitlements === 'function') { try { await refreshEntitlements(); } catch (_) {} }
        bumpHomeUsage(count || 1);
        incrementFreeQuestionsUsed(count);
        const nowUsed = getFreeQuestionsUsed();
        setFreeQuestionsUsed(nowUsed);
        const hasPaidTierNow = !!(entitlements?.tier && entitlements.tier !== 'free');
        if (nowUsed >= FREE_TRIAL_TOTAL && !hasPaidTierNow) setShowClinicalGate(true);
        if (trackGenerationForPromo()) {
            setPromoVariant('enjoying');
            setShowPromoModal(true);
        }
    };

    // "Done" in the modal — the modal itself already wrote the cards straight into whichever
    // folder was chosen (same pattern as Anki import), so this is just closing up and letting
    // the user know. Folder cardCount updates reactively via the existing folders subscription.
    const handleDocumentModalDone = ({ folderId: savedFolderId, count } = {}) => {
        if (count > 0) {
            const folderName = folders.find((f) => f.id === savedFolderId)?.name;
            pushToast('success', `${count} flashcard${count === 1 ? '' : 's'} saved${folderName ? ` to ${folderName}` : ''}`);
        }
        setShowDocumentStudyModal(false);
        setDocumentStudyFile(null);
        setStudyDocument(null);
        setDocumentStudyInitialPage(null);
    };

    // camera capture
    const startCamera = async () => {
        try {
            setCameraErr('');
            const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: 'environment' } }, audio: false });
            // Mark camera on immediately so the <video> mounts
            setIsCameraOn(true);
            const attach = () => {
                const v = videoRef.current;
                if (!v) { requestAnimationFrame(attach); return; }
                try {
                    v.srcObject = stream;
                    v.muted = true;
                    v.playsInline = true;
                    v.autoplay = true;
                    const p = v.play();
                    if (p && typeof p.catch === 'function') p.catch(() => { });
                } catch (_) { }
            };
            attach();
        } catch (e) {
            setCameraErr(e.message || 'Camera not available');
        }
    };
    const stopCamera = () => {
        const stream = videoRef.current?.srcObject;
        stream?.getTracks()?.forEach(t => t.stop());
        setIsCameraOn(false);
    };
    const takePicture = () => {
        if (!videoRef.current || !canvasRef.current) return;
        const v = videoRef.current;
        const c = canvasRef.current;
        const ctx = c.getContext('2d');
        c.width = v.videoWidth; c.height = v.videoHeight;
        ctx.drawImage(v, 0, 0, c.width, c.height);
        const dataUrl = c.toDataURL('image/jpeg', 1.0);
        stopCamera();
        setImagePreview(dataUrl);
        try { lastBase64Ref.current = dataUrl.split(',')[1]; } catch (_) { }
        analyzeImage(lastBase64Ref.current);
    };

    // Build credits string locally to keep exact pill styling
    const balance = typeof entitlements?.creditsBalance === 'number' ? entitlements.creditsBalance : null;
    const tier = entitlements?.tier || null;
    const creditsString = isDevMode
        ? 'unlimited'
        : balance != null
            ? `${balance} credits${tier ? ' • ' + tier : ''}`
            : `${remaining} credits (Free Trial)`;
    
    const effectiveCredits = balance != null ? balance : (isDevMode ? Infinity : remaining);

    // FlashFonic panel component that consumes context INSIDE provider
    const FlashFonicPanel = () => {
        const ff = useFlashFonic();
        const [uploadDuration, setUploadDuration] = useState(15);
        const [isGenUpload, setIsGenUpload] = useState(false);
        const [fonicSubMode, setFonicSubMode] = useState(() => {
            try { return localStorage.getItem('ff:fonicSubMode') === 'upload' ? 'upload' : 'live'; } catch (_) { return 'live'; }
        }); // 'live' | 'upload'
        useEffect(() => {
            if (fonicSubMode === 'upload' && ff?.isListening) {
                try { ff.stopListening(); } catch (_) { }
            }
            try { localStorage.setItem('ff:fonicSubMode', fonicSubMode); } catch (_) { }
        }, [fonicSubMode]);
        const handleProcessAudio = async () => {
            if (!upload.uploadedFile) return;
            upload.setIsProcessing(true);
            flashFonicNotify('Uploading and processing audio...');
            try {
                const reader = new FileReader();
                reader.readAsDataURL(upload.uploadedFile);
                reader.onloadend = async () => {
                    const base64File = (reader.result || '').toString().split(',')[1];
                    try {
                        const data = await authApiIdempotent(`${API_BASE_URL}/process-audio`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ audio_data: base64File, mime_type: (upload.uploadedFile && upload.uploadedFile.type) || '' }) });
                        upload.setAudioCacheId(data.audioId);
                        flashFonicNotify('Audio is ready! You can now flash it!');
                    } catch (e) {
                        console.error('[AppHome] process-audio failed:', { code: e?.code, requestId: e?.requestId, status: e?.status });
                        flashFonicNotify(e.message || 'Process failed');
                    }
                    finally { upload.setIsProcessing(false); }
                };
            } catch (_) { upload.setIsProcessing(false); }
        };
        const handleUploadFlash = async () => {
            try {
                if (!isDevMode && Number(entitlements?.creditsBalance ?? 0) <= 0) {
                    flashFonicNotify('Not enough credits. Upgrade to keep flashing!');
                    return;
                }
                setIsGenUpload(true);
                const activePlayer = upload.fileType === 'video' ? upload.videoPlayerRef.current : (upload.audioPlayerRef.current || (await import('../lib/globalAudio.js')).default.get());
                const savedPlayback = activePlayer ? (await import('../lib/globalAudio.js')).default.captureState() : { wasPlaying: false, currentTime: 0 };
                // Immediate restore in case UI reflows or event listeners cause a pause
                try { (await import('../lib/globalAudio.js')).default.restoreState(savedPlayback); } catch (_) { }
                const requestBody = { startTime: activePlayer?.currentTime || 0, duration: uploadDuration, is_live_capture: false };
                if (upload.audioCacheId) {
                    requestBody.audioId = upload.audioCacheId;
                } else if (upload.uploadedFile) {
                    const reader = new FileReader();
                    reader.readAsDataURL(upload.uploadedFile);
                    await new Promise((resolve) => {
                        reader.onloadend = () => {
                            requestBody.audio_data = (reader.result || '').toString().split(',')[1];
                            try { requestBody.mime_type = upload.uploadedFile.type || ''; } catch { }
                            resolve();
                        };
                    });
                } else {
                    return;
                }
                await generateFlashcardRequestLive(requestBody);
                // Restore playback (time and play state) after request
                if (activePlayer) {
                    const ga = (await import('../lib/globalAudio.js')).default;
                    ga.restoreState(savedPlayback);
                }
            } finally { setIsGenUpload(false); }
        };
        return (
            <React.Fragment>
                {/* Sub-toggle: Live vs Upload */}
                <div className="mb-5 inline-flex rounded-2xl p-1 bg-slate-900/70 border border-white/10 shadow-inner">
                    <button type="button" onClick={() => setFonicSubMode('live')} className={`px-4 sm:px-5 py-2 rounded-xl text-sm font-medium ${fonicSubMode === 'live' ? 'text-white bg-gradient-to-r from-teal-600 to-emerald-600 shadow-md shadow-teal-500/20' : 'text-slate-300 hover:text-white'}`}>Live capture</button>
                    <button type="button" onClick={() => setFonicSubMode('upload')} className={`px-4 sm:px-5 py-2 rounded-xl text-sm font-medium ${fonicSubMode === 'upload' ? 'text-white bg-gradient-to-r from-teal-600 to-emerald-600 shadow-md shadow-teal-500/20' : 'text-slate-300 hover:text-white'}`}>Upload audio/video</button>
                </div>

                {fonicSubMode === 'live' && (
                    <div className="mx-auto grid gap-6 lg:gap-8 grid-cols-1 lg:grid-cols-2 max-w-6xl items-start">
                        <div className="h-full w-full max-w-[780px] rounded-3xl bg-slate-900/60 border border-white/10 p-6 sm:p-8 shadow-2xl shadow-black/30 justify-self-center lg:justify-self-stretch">
                            <FonicLiveCapture
                                targetFolderId={targetFolderId}
                                onTargetFolderChange={setTargetFolderId}
                                folders={folders}
                                onCreateFolder={() => setIsCreateOpen(true)}
                                isMovingQueueCards={isMovingQueueCards}
                            />
                        </div>
                        {/* Desktop Review Queue aside for Live */}
                        {renderDesktopReviewQueue()}
                    </div>
                )}
                {/* Upload-based FlashFonic */}
                {fonicSubMode === 'upload' && (
                    <div className="mt-2 grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-6xl mx-auto">
                        <div className="rounded-3xl bg-slate-900/60 border border-white/10 p-6 sm:p-8 shadow-2xl shadow-black/30">
                            <h4 className="text-lg font-semibold tracking-tight mb-3">Create from audio/video</h4>
                            <UploadControls
                                isDevMode={isDevMode}
                                entitlements={entitlements}
                                fileName={upload.fileName}
                                triggerFileUpload={upload.triggerFileUpload}
                                fileInputRef={upload.fileInputRef}
                                handleFileChange={upload.handleFileChange}
                                mediaSrc={upload.mediaSrc}
                                handleCancelUpload={upload.handleCancelUpload}
                                fileType={upload.fileType}
                                videoPlayerRef={upload.videoPlayerRef}
                                audioPlayerRef={upload.audioPlayerRef}
                                togglePlayPause={upload.togglePlayPause}
                                isPlaying={upload.isPlaying}
                                handleSeek={upload.handleSeek}
                                currentTime={upload.currentTime}
                                mediaDuration={upload.mediaDuration}
                                isProcessing={upload.isProcessing}
                                handleProcessAudio={handleProcessAudio}
                                audioCacheId={upload.audioCacheId}
                                isUploadAutoFlashOn={upload.isUploadAutoFlashOn}
                                setIsUploadAutoFlashOn={upload.setIsUploadAutoFlashOn}
                                uploadAutoFlashInterval={upload.uploadAutoFlashInterval}
                                setUploadAutoFlashInterval={upload.setUploadAutoFlashInterval}
                                duration={uploadDuration}
                                setDuration={setUploadDuration}
                                isGenerating={isGenUpload}
                                handleUploadFlash={handleUploadFlash}
                                rebuildPreview={upload.rebuildPreview}
                            />
                        </div>
                        {renderDesktopReviewQueue()}
                    </div>
                )}
                {/* Mobile Review Queue - between controls and folders (FlashFonic) */}
                {renderMobileQueue('mt-6')}

                {/* Move Folders section directly under Review Queue (full width on desktop) */}
                {renderAppHomeFoldersSection({
                    showLoadingSkeleton: true,
                    hasMoreFolders,
                    onLoadMore: () => setPage((p) => p + 1),
                })}
                {ff?.isListening && (
                    <div className="fixed bottom-6 right-6 z-50 inline-flex items-center gap-2 rounded-full bg-emerald-500/15 text-emerald-200 px-3.5 py-2 ring-1 ring-emerald-400/30 shadow-lg shadow-emerald-700/20">
                        <span className="h-2.5 w-2.5 rounded-full bg-emerald-400 animate-pulse"></span>
                        Listening
                    </div>
                )}
                {/* Slider visuals handled reactively above; removing legacy script */}
            </React.Fragment>
        );
    };

    const generateClinicalFlashcardRequest = async (requestBody) =>
        generateFlashcardRequestLive({
            ...requestBody,
            clinical: buildClinicalPayload(),
        });

    // Hydrate persisted UI settings
    useEffect(() => {
        // Default is clinical; only override if explicitly set to foto/fonic via query param (legacy)
        try {
            const pm = new URLSearchParams(window.location.search).get('mode');
            if (pm === 'fonic' || pm === 'foto') setProductMode(pm);
        } catch (_) {}
    }, []);
    useEffect(() => {
        try {
            localStorage.setItem('ff:clinicalSettings', JSON.stringify(clinicalSettings));
        } catch (_) {}
    }, [clinicalSettings]);
    // Remove top-level ff persistence block (context is consumed inside the panel)

    const [pendingCreditConfirm, setPendingCreditConfirm] = useState(null);

    useEffect(() => {
        try {
            if (homeQueued.length > 0 && homeQueueRef.current) {
                homeQueueRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        } catch { }
    }, [homeQueued.length]);

    const clinicalQueuedCards = useMemo(
        () => homeQueued.filter(isClinicalQueueCard),
        [homeQueued]
    );

    const moveSelectedQueueCardsToFolder = useCallback(async (explicitCardIds) => {
        if (isMovingQueueCardsRef.current) return;

        const selectedIds =
            Array.isArray(explicitCardIds) && explicitCardIds.length > 0
                ? explicitCardIds
                : homeQueued.filter((q) => selectedQueue[q.id]).map((q) => q.id);
        if (selectedIds.length === 0) { pushToast('info', 'Select cards to move'); return; }
        if (!targetFolderId) { pushToast('info', 'Choose a target folder'); return; }
        const db = getDb();
        if (!db || !uid) { pushToast('error', 'Please sign in to save'); return; }

        isMovingQueueCardsRef.current = true;
        setIsMovingQueueCards(true);

        try {
            const selectedSet = new Set(selectedIds.map(id => String(id)));
            const cardsToMove = homeQueued.filter(q => selectedSet.has(String(q.id)));
            const destCards = await fetchCardsForFolder(targetFolderId);
            const existing = buildCardKeySet(destCards || []);
            const { unique: uniqueCandidates, duplicates } = partitionUniqueCards(cardsToMove, existing);
            const movedSourceIds = new Set();
            let failed = 0;
            for (const c of uniqueCandidates) {
                try {
                    const payload = { question: (typeof c.question === 'string' ? c.question : ''), answer: (typeof c.answer === 'string' ? c.answer : ''), isFlagged: !!c.isFlagged, lastViewed: c.lastViewed || null, createdAt: serverTimestamp(), _clientCreatedAt: Date.now(), ...clinicalCardLinkFields(c) };
                    const cid = `${Date.now()}.${Math.floor(Math.random() * 10000)}`;
                    await setDoc(doc(db, 'users', uid, 'folders', targetFolderId, 'cards', cid), payload);
                    movedSourceIds.add(String(c.id));
                } catch (e) { failed += 1; }
            }
            const movedCount = movedSourceIds.size;
            setHomeQueued(prev => prev.filter(q => !movedSourceIds.has(String(q.id))));
            setSelectedQueue({});
            if (movedCount > 0) setFolders(prev => prev.map(f => f.id === targetFolderId ? { ...f, cardCount: (f.cardCount || 0) + movedCount } : f));
            pushToast(movedCount ? 'success' : 'info', `Moved ${movedCount} card${movedCount === 1 ? '' : 's'}${duplicates ? `, skipped ${duplicates} duplicate${duplicates === 1 ? '' : 's'}` : ''}${failed ? `, ${failed} failed` : ''}`);
        } catch (e) {
            pushToast('error', e.message || 'Move failed');
        } finally {
            isMovingQueueCardsRef.current = false;
            setIsMovingQueueCards(false);
        }
    }, [homeQueued, selectedQueue, targetFolderId, uid]);

    const handleClinicalSaveToFolder = useCallback(() => {
        const hasSelection = clinicalQueuedCards.some((c) => selectedQueue[c.id]);
        const ids = hasSelection
            ? clinicalQueuedCards.filter((c) => selectedQueue[c.id]).map((c) => c.id)
            : clinicalQueuedCards.map((c) => c.id);
        if (ids.length === 0) {
            pushToast('info', 'No cards to save');
            return;
        }
        if (!targetFolderId) {
            pushToast('info', 'Choose a folder to save to');
            return;
        }
        return moveSelectedQueueCardsToFolder(ids);
    }, [clinicalQueuedCards, selectedQueue, targetFolderId, moveSelectedQueueCardsToFolder]);

    const renderReviewQueuePanel = useCallback((options = {}) => {
        const { showClear = false, maxHeightClass = 'max-h-[60vh]' } = options;
        return (
            <AppHomeReviewQueue
                homeQueued={homeQueued}
                selectedQueue={selectedQueue}
                onSelectedQueueChange={setSelectedQueue}
                folders={folders}
                targetFolderId={targetFolderId}
                onTargetFolderChange={setTargetFolderId}
                isMovingQueueCards={isMovingQueueCards}
                onMoveSelected={moveSelectedQueueCardsToFolder}
                editingCardId={editingCardId}
                onEditingCardIdChange={setEditingCardId}
                pendingDeleteId={pendingDeleteId}
                onPendingDeleteIdChange={setPendingDeleteId}
                onUpdateCard={(id, updated) => {
                    setHomeQueued((prev) =>
                        prev.map((cc) =>
                            cc.id === id
                                ? { ...cc, question: updated.question, answer: updated.answer }
                                : cc
                        )
                    );
                    pushToast('success', 'Card updated');
                }}
                onRemoveCard={(id) => {
                    setHomeQueued((prev) => prev.filter((x) => x.id !== id));
                    setSelectedQueue((prev) => {
                        const next = { ...prev };
                        delete next[id];
                        return next;
                    });
                    setPendingDeleteId(null);
                    pushToast('success', 'Card removed');
                }}
                onClearQueue={() => {
                    setHomeQueued([]);
                    setSelectedQueue({});
                }}
                showClear={showClear}
                maxHeightClass={maxHeightClass}
            />
        );
    }, [
        folders,
        homeQueued,
        selectedQueue,
        targetFolderId,
        isMovingQueueCards,
        moveSelectedQueueCardsToFolder,
        editingCardId,
        pendingDeleteId,
    ]);

    const renderDesktopReviewQueue = useCallback(
        () => (
            <div ref={homeQueueRef} className="hidden lg:block">
                {renderReviewQueuePanel({ maxHeightClass: 'max-h-[60vh]' })}
            </div>
        ),
        [renderReviewQueuePanel]
    );

    const renderClinicalGeneratedCards = useCallback(
        () =>
            clinicalQueuedCards.length > 0 ? (
                <ClinicalGeneratedCards
                    cards={clinicalQueuedCards}
                    selectedMap={selectedQueue}
                    onSelectedMapChange={setSelectedQueue}
                    folders={folders}
                    targetFolderId={targetFolderId}
                    onTargetFolderChange={setTargetFolderId}
                    onSaveToFolder={handleClinicalSaveToFolder}
                    isSaving={isMovingQueueCards}
                />
            ) : null,
        [
            clinicalQueuedCards,
            selectedQueue,
            folders,
            targetFolderId,
            handleClinicalSaveToFolder,
            isMovingQueueCards,
        ]
    );

    const renderMobileQueue = useCallback(
        (className = 'mt-5') => (
            <div ref={homeQueueRef} className={`lg:hidden ${className}`}>
                {renderReviewQueuePanel({ showClear: true, maxHeightClass: 'max-h-[50vh]' })}
            </div>
        ),
        [renderReviewQueuePanel]
    );


    const savePreviewCardEdit = async (updatedCard) => {
        if (!previewFolder) return;
        try {
            const db = getDb();
            const currentUid = getCurrentUid();
            if (!db || !currentUid) { pushToast('error', 'Please sign in to edit'); return; }
            await updateDoc(doc(db, 'users', currentUid, 'folders', previewFolder.id, 'cards', String(updatedCard.id)), {
                question: updatedCard.question || '',
                answer: updatedCard.answer || ''
            });
            setPreviewFolder(prev => prev ? { ...prev, cards: (prev.cards || []).map(c => c.id === updatedCard.id ? { ...c, question: updatedCard.question, answer: updatedCard.answer } : c) } : prev);
            setFolders(curr => curr.map(f => f.id === previewFolder.id ? { ...f, cards: (f.cards || []).map(c => c.id === updatedCard.id ? { ...c, question: updatedCard.question, answer: updatedCard.answer } : c) } : f));
            setPreviewEditingCardId(null);
            pushToast('success', 'Card updated');
        } catch (_) { pushToast('error', 'Edit failed'); }
    };

    return (
        <div className="min-h-full text-slate-200" style={{ backgroundColor: 'var(--bg-page)' }}>
            {showSettingsModal && (
                <SettingsModal
                    onClose={() => setShowSettingsModal(false)}
                    clinicalSettings={clinicalSettings}
                    onClinicalSettingsChange={setClinicalSettings}
                    entitlements={entitlements}
                    onOpenAccount={onOpenAccount}
                    onOpenPlanChooser={onOpenPlanChooser}
                />
            )}

            {/* Onboarding wizard — fires once after signup */}
            {showDashboard && (
                <PerformanceDashboard
                    onClose={() => setShowDashboard(false)}
                    entitlements={entitlements}
                    isDevMode={isDevMode}
                />
            )}

            {showOnboarding && (
                <OnboardingWizard
                    onComplete={(profile) => {
                        completeOnboarding(profile);
                        const { examType } = resolveClampedExamType({
                            examTarget: profile.examTarget,
                            subjects: profile.subjects,
                            isMultiSubject: profile.focus === 'class_prep',
                            entitlementsTier: entitlements?.tier,
                        });
                        if (examType) setClinicalSettings((prev) => ({ ...prev, examType }));
                        setShowOnboarding(false);
                    }}
                />
            )}

            {/* Promotional modal — triggered after N generations */}
            {showPromoModal && (
                <PromotionalModal
                    variant={promoVariant}
                    onClose={() => setShowPromoModal(false)}
                    onGoToPricing={() => { setShowPromoModal(false); if (onOpenPlanChooser) onOpenPlanChooser(); }}
                    onSignUp={() => { setShowPromoModal(false); if (onOpenAccount) onOpenAccount(); }}
                />
            )}

            {/* Clinical free-trial gate — shown when trial is exhausted in clinical mode.
                Guarded at render time (not just at the setState call sites) so a paid/comped
                tier can never see it, even if showClinicalGate was already set to true before
                entitlements finished loading (e.g. during initial mount). */}
            {showClinicalGate && productMode === 'clinical' && !(entitlements?.tier && entitlements.tier !== 'free') && (
                <ClinicalGateModal
                    isAnonymous={!user || user.isAnonymous}
                    onUpgrade={() => { setShowClinicalGate(false); onOpenPlanChooser?.({ isClinicals: true }); }}
                    onSignUp={() => { setShowClinicalGate(false); onOpenAccount?.(); }}
                    onClose={() => setShowClinicalGate(false)}
                />
            )}

            {/* Background accents — teal/emerald brand glow in both product modes */}
            <div aria-hidden="true" className="fixed inset-0 overflow-hidden pointer-events-none">
                <div className="absolute -top-24 -left-16 h-72 w-72 rounded-full bg-teal-600/20 blur-3xl" />
                <div className="absolute top-1/3 -right-16 h-72 w-72 rounded-full bg-emerald-500/15 blur-3xl" />
                <div className="absolute bottom-0 left-1/2 -translate-x-1/2 h-72 w-[36rem] bg-gradient-to-r from-teal-500/10 to-emerald-500/10 blur-3xl rounded-full" />
            </div>

            {/* Page */}
            <div className="relative min-h-full flex flex-col px-4 sm:px-6 lg:px-12 pb-20 sm:pb-0">
                {/* Header — brand transforms based on active product mode */}
                <header className="w-full">
                    <div className="mx-auto w-full max-w-[1600px] px-2 sm:px-3 lg:px-4">
                        <div className="flex items-center justify-between py-6">
                            <a href="/" className="flex items-center gap-3">
                                <div className="leading-tight">
                                    {productMode === 'clinical' ? (
                                        <>
                                            <h1 className="text-[28px] sm:text-[32px] tracking-tight font-semibold">
                                                <span className="bg-gradient-to-r from-teal-400 via-emerald-400 to-cyan-300 bg-clip-text text-transparent">FlashFonic</span>
                                                <span className="align-top text-teal-400/70 text-lg">™</span>
                                            </h1>
                                            <p className="text-slate-400 text-sm sm:text-[13px]">Exam prep built for clinicians, by clinicians.</p>
                                        </>
                                    ) : (
                                        <>
                                            <h1 className="text-[28px] sm:text-[32px] tracking-tight font-semibold">
                                                <span className="bg-gradient-to-r from-teal-400 to-emerald-400 bg-clip-text text-transparent">FlashFonic</span>
                                                <span className="align-top text-teal-400/80 text-lg">™</span>
                                            </h1>
                                            <p className="text-slate-400 text-sm sm:text-[13px]">Listen. Flash it. Learn.</p>
                                        </>
                                    )}
                                </div>
                            </a>
                            {/* All three controls share the same pill sizing (compact on mobile,
                                roomier from sm: up) so nothing looks mismatched at any width —
                                Upgrade/Subscribe gets a teal tint to stay the visually primary
                                one without breaking that uniformity. */}
                            <nav className="flex flex-wrap items-center justify-end gap-1.5 sm:gap-3 lg:gap-4">
                                {onOpenPlanChooser && (
                                    <button
                                        onClick={() => onOpenPlanChooser({ isClinicals: productMode === 'clinical' })}
                                        className="inline-flex items-center rounded-full px-3 py-1.5 sm:px-3.5 sm:py-2 text-xs sm:text-sm font-medium text-teal-200 bg-teal-500/10 ring-1 ring-teal-400/30 hover:bg-teal-500/15 transition"
                                    >
                                        {productMode === 'clinical' ? 'Upgrade' : 'Subscribe'}
                                    </button>
                                )}
                                <button
                                    onClick={() => setShowSettingsModal(true)}
                                    title="Settings"
                                    aria-label="Settings"
                                    className="hidden sm:inline-flex items-center gap-2 rounded-full px-3.5 py-2 text-sm font-medium bg-white/5 hover:bg-white/10 ring-1 ring-white/10 transition"
                                >
                                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"/><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/></svg>
                                    Settings
                                </button>
                                {onOpenAccount && (
                                    <button
                                        onClick={onOpenAccount}
                                        className="inline-flex items-center rounded-full px-3 py-1.5 sm:px-3.5 sm:py-2 text-xs sm:text-sm font-medium bg-white/5 hover:bg-white/10 ring-1 ring-white/10 transition"
                                    >
                                        Account
                                    </button>
                                )}
                            </nav>
                        </div>
                        {/* Mobile credit counter, right under the Account button — unconditional
                            on sign-in state, since guests/anonymous users have credits too and
                            the signed-in-only chips below don't render for them at all. Guests
                            also get the Settings gear here, since the desktop-only Settings pill
                            is hidden on mobile and the signed-in chip row (with its own gear)
                            doesn't render for them either — otherwise they'd have no mobile path
                            to Settings at all. */}
                        <div className="sm:hidden w-full flex justify-center items-center gap-2 mt-2">
                            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/5 ring-1 ring-white/10 px-3 py-1 text-[11px] text-slate-200">
                                <span className="text-teal-300" aria-hidden>⚡</span>
                                {isDevMode ? 'Unlimited credits' : `${effectiveCredits} credit${effectiveCredits === 1 ? '' : 's'}`}
                            </span>
                            {(!user || user.isAnonymous) && (
                                <button
                                    onClick={() => setShowSettingsModal(true)}
                                    title="Settings"
                                    aria-label="Settings"
                                    className="shrink-0 inline-flex items-center justify-center h-8 w-8 rounded-full bg-teal-500/15 hover:bg-teal-500/25 ring-1 ring-teal-400/30 text-teal-200 transition"
                                >
                                    <svg className="h-[17px] w-[17px]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"/><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/></svg>
                                </button>
                            )}
                        </div>
                        {user && !user.isAnonymous && (
                            <div className="hidden sm:flex justify-end mt-2.5">
                                <div className="inline-flex items-center gap-2 rounded-full bg-white/5 ring-1 ring-white/10 pl-1.5 pr-3 py-1.5">
                                    <span className="h-6 w-6 shrink-0 rounded-full bg-gradient-to-br from-teal-400 to-emerald-500 flex items-center justify-center text-[11px] font-bold text-slate-900">
                                        {(user.email || user.displayName || '?').charAt(0).toUpperCase()}
                                    </span>
                                    <span className="text-xs text-slate-300 max-w-[200px] truncate">{user.email || user.displayName}</span>
                                    <span className={`text-[10px] font-medium rounded-full px-1.5 py-0.5 shrink-0 ${getTierBadge(entitlements?.tier).className}`}>{getTierBadge(entitlements?.tier).label}</span>
                                </div>
                            </div>
                        )}
                        {user && !user.isAnonymous && (
                            <div className="sm:hidden w-full flex justify-center items-center gap-1.5 mt-2 mb-1">
                                <span
                                    className="inline-flex max-w-[70%] items-center gap-1.5 rounded-full pl-1 pr-2.5 py-1 text-[11px] text-slate-200 bg-white/5 ring-1 ring-white/10 truncate"
                                    title={user.email || user.displayName || 'Account'}
                                >
                                    <span className="h-5 w-5 shrink-0 rounded-full bg-gradient-to-br from-teal-400 to-emerald-500 flex items-center justify-center text-[9px] font-bold text-slate-900">
                                        {(user.email || user.displayName || '?').charAt(0).toUpperCase()}
                                    </span>
                                    <span className="truncate">{user.email || user.displayName || 'Account'}</span>
                                </span>
                                <span className={`text-[10px] font-medium rounded-full px-1.5 py-0.5 shrink-0 ${getTierBadge(entitlements?.tier).className}`}>{getTierBadge(entitlements?.tier).label}</span>
                                <button
                                    onClick={() => setShowSettingsModal(true)}
                                    title="Settings"
                                    aria-label="Settings"
                                    className="shrink-0 inline-flex items-center justify-center h-9 w-9 rounded-full bg-teal-500/15 hover:bg-teal-500/25 ring-1 ring-teal-400/30 text-teal-200 transition"
                                >
                                    <svg className="h-[18px] w-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"/><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/></svg>
                                </button>
                            </div>
                        )}
                    </div>
                </header>

                {/* Main */}
                <main className="flex-1">
                    <section className="mx-auto w-full max-w-[1600px] px-2 sm:px-3 lg:px-4 py-4 sm:py-8">
                        {/* Dashboard hero */}
                        {productMode === 'clinical' ? (
                            <div className="flex items-center gap-3">
                                <h2 className="text-lg font-semibold text-white">Workspace</h2>
                            </div>
                        ) : (
                            <HomeDashboard
                                user={user}
                                folders={folders}
                                mostRecentScore={mostRecentScore}
                                entitlements={entitlements}
                                productMode={productMode}
                                onSetProductMode={setProductMode}
                                onStudyFolder={openStudy}
                                onPlayGame={handlePlayGame}
                                onLaunchNemesis={handleLaunchNemesis}
                                onStartExam={handleStartExam}
                            />
                        )}


                        {/* Main product area */}
                        {productMode === 'foto' ? (
                            <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-6xl mx-auto items-start">
                                <div className="rounded-3xl bg-slate-900/60 border border-white/10 p-5 sm:p-7 shadow-2xl shadow-black/30">
                                    <div className="flex items-start gap-3">
                                        <div>
                                            <h3 className="text-xl font-semibold tracking-tight">Create from a photo</h3>
                                            <p className="mt-1 text-sm text-slate-400">Choose your default number of flashcards and upload an image of your notes.</p>
                                        </div>
                                    </div>

                                    {/* Dropzone */}
                                    <div
                                        className={`mt-6 relative border-2 border-dashed rounded-2xl ${dragActive ? 'border-teal-600/50' : 'border-slate-700'} bg-slate-950/40`}
                                        onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
                                        onDragEnter={(e) => { e.preventDefault(); setDragActive(true); }}
                                        onDragLeave={(e) => { e.preventDefault(); setDragActive(false); }}
                                        onDrop={onDrop}
                                    >
                                        <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileChange} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" />
                                        <div className="p-6 sm:p-8 text-center">
                                            {isGenerating && !imagePreview ? (
                                                <div className="animate-pulse h-48 w-full rounded-xl bg-slate-800/50 border border-white/10"></div>
                                            ) : imagePreview ? (
                                                <img src={imagePreview} alt="Preview" className="mx-auto h-48 w-full object-cover rounded-xl border border-white/10" />
                                            ) : (
                                                <>
                                                    <div className="mx-auto h-12 w-12 rounded-xl bg-slate-800/70 border border-white/10 flex items-center justify-center">
                                                        <svg viewBox="0 0 24 24" width="20" height="20" className="text-slate-300" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
                                                    </div>
                                                    <div className="mt-4 text-sm">
                                                        <span className="text-white font-medium">Upload</span>
                                                        <span className="text-slate-400"> an image here, or</span>
                                                        <button type="button" onClick={(e) => { e.preventDefault(); openFilePicker(); }} className="ml-1 text-teal-300 hover:text-teal-200 underline underline-offset-4">browse</button>
                                                    </div>
                                                    <p className="text-xs text-slate-500 mt-1">PNG, JPG up to 10MB</p>
                                                </>
                                            )}
                                        </div>
                                    </div>

                                    {/* Camera - removed start button per request */}
                                    {isCameraOn && (
                                        <div className="mt-4 space-y-2">
                                            <video ref={videoRef} autoPlay playsInline muted className="w-full rounded-xl border border-white/10" style={{ width: '100%', height: 'auto', objectFit: 'contain' }} />
                                            <p className="text-xs text-slate-400">Tip: Point your camera at well-lit notes. On iOS, ensure Safari has camera permission in Settings.</p>
                                            <div className="flex gap-2">
                                                <button onClick={takePicture} className="inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2 text-white bg-gradient-to-r from-teal-600 to-emerald-600">Snap</button>
                                                <button onClick={stopCamera} className="inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2 text-slate-200 bg-slate-800/70 border border-white/10">Stop</button>
                                            </div>
                                            <canvas ref={canvasRef} className="hidden" />
                                        </div>
                                    )}
                                    {cameraErr && <p className="text-xs text-rose-300 mt-2">{cameraErr}</p>}

                                    {/* Upload/Camera actions (desktop shows camera) */}
                                    {!isCameraOn && (
                                        <div className="mt-6 flex justify-center gap-3">
                                            <button type="button" onClick={openFilePicker} className="inline-flex items-center justify-center gap-2 rounded-xl px-6 py-3.5 text-white font-medium bg-gradient-to-r from-teal-600 to-emerald-600 hover:from-teal-500 hover:to-emerald-500 shadow-lg shadow-teal-500/20 disabled:opacity-60" disabled={isGenerating}>
                                                <svg viewBox="0 0 24 24" width="18" height="18" className="text-white" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19v-2a4 4 0 0 0-4-4H7" /><path d="M7 7l-5 5 5 5" /></svg>
                                                Upload Photo
                                            </button>
                                            <button type="button" onClick={() => { try { startCamera(); } catch (_) { } }} className="inline-flex items-center justify-center gap-2 rounded-xl px-6 py-3.5 text-slate-200 font-medium bg-slate-800/70 hover:bg-slate-800 ring-1 ring-white/10">
                                                <svg viewBox="0 0 24 24" width="18" height="18" className="text-slate-200" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h3l2-3h8l2 3h3a2 2 0 0 1 2 2z" /><circle cx="12" cy="13" r="4" /></svg>
                                                Use Camera
                                            </button>
                                        </div>
                                    )}
                                    {isCameraOn && (
                                        <div className="mt-6 flex justify-center">
                                            <span className="inline-flex items-center justify-center rounded-xl px-6 py-3.5 text-xs text-emerald-300 bg-emerald-600/15 ring-1 ring-emerald-400/20">Camera active</span>
                                        </div>
                                    )}

                                    {/* Flash It + Manage Credits */}
                                    <div className="mt-4 flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3 sm:gap-4">
                                        <button type="button" onClick={() => handleAutoGenerate(undefined)} className={`flash-it-button ${imagePreview && !isGenerating ? 'animated' : ''} inline-flex items-center justify-center gap-2 rounded-2xl px-8 py-4 font-semibold disabled:opacity-60`} disabled={isGenerating || !imagePreview || !(effectiveCredits > 0)}>
                                            ⚡ Flash It!
                                        </button>
                                        {!(effectiveCredits > 0) && (
                                            <button onClick={onOpenPlanChooser} className="inline-flex items-center gap-2 rounded-xl px-4 py-3 text-xs text-white bg-gradient-to-r from-teal-600 to-emerald-600">Manage Credits</button>
                                        )}
                                    </div>

                                    {/* Count mode toggle + Slider */}
                                    <div className="mt-7">
                                        {/* Removed explicit AI toggle per client request; we'll show a modal on Flash It */}
                                        <div className="flex items-center justify-between">
                                            <p className="text-sm text-slate-300">Number of Flashcards</p>
                                            <div className="text-right">
                                                <p className="text-sm font-medium text-teal-300">{cardCount}</p>
                                                {aiAnalysis?.recommendation != null && (
                                                    <p className="text-[11px] text-slate-500">Suggested: <span className="text-teal-300 font-medium">{aiAnalysis.recommendation}</span></p>
                                                )}
                                            </div>
                                        </div>
                                        <div className="mt-3 relative">
                                            <div className="h-2 w-full rounded-full bg-slate-800/80"></div>
                                            <div className="absolute inset-y-0 left-0 h-2 rounded-full bg-gradient-to-r from-teal-600 to-emerald-600" style={{ width: `${((cardCount - 1) / 19) * 100}%` }}></div>
                                            <input
                                                type="range"
                                                min="1"
                                                max="20"
                                                step="1"
                                                value={cardCount}
                                                onInput={(e) => setCardCount(parseInt(e.target.value, 10))}
                                                onChange={(e) => setCardCount(parseInt(e.target.value, 10))}
                                                className="absolute inset-0 w-full h-2 opacity-0 cursor-pointer"
                                                aria-label="Flashcard count"
                                            />
                                            <div className="absolute -top-[6px]" style={{ left: `calc(${((cardCount - 1) / 19) * 100}% - 10px)` }}>
                                                <div className="h-5 w-5 rounded-full bg-white shadow-lg shadow-teal-500/20 ring-4 ring-teal-500/30"></div>
                                            </div>
                                            <div className="mt-6 flex justify-between text-[11px] text-slate-500"><span>1</span><span>5</span><span>10</span><span>15</span><span>20</span></div>
                                        </div>
                                        {statusMsg && (<p className="mt-3 text-xs text-slate-400">{statusMsg}</p>)}
                                    </div>

                                    {/* Save target controls removed per client request; use Review Queue move instead */}
                                </div>

                                {/* Desktop Review Queue aside for FlashFoto */}
                                {renderDesktopReviewQueue()}

                                {/* Right: Tips (moved to end of page per request) */}
                                <div className="hidden">
                                    <div className="flex items-start gap-4 mb-6">
                                        <div className="h-12 w-12 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg shadow-emerald-500/20">
                                            <svg viewBox="0 0 24 24" width="20" height="20" className="text-white" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M9 11H5a2 2 0 0 0-2 2v3c0 1.1.9 2 2 2h2" />
                                                <path d="M20 12a9 9 0 1 1-6.18-8.49" />
                                                <path d="M9 12h4l-2 2" />
                                                <path d="M11 10l-2-2" />
                                            </svg>
                                        </div>
                                        <div>
                                            <h4 className="text-xl font-semibold tracking-tight text-white">Best results</h4>
                                            <p className="mt-1 text-sm text-slate-400">Follow these tips for optimal flashcard generation</p>
                                        </div>
                                    </div>

                                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
                                        <li className="flex items-center gap-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20 px-3 py-2">
                                            <span className="h-5 w-5 rounded-full bg-emerald-500/30 flex items-center justify-center">
                                                <svg viewBox="0 0 24 24" width="12" height="12" className="text-emerald-300" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="m9 12 2 2 4-4" />
                                                </svg>
                                            </span>
                                            <span className="text-sm font-medium text-emerald-200">Good lighting</span>
                                        </li>
                                        <li className="flex items-center gap-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20 px-3 py-2">
                                            <span className="h-5 w-5 rounded-full bg-emerald-500/30 flex items-center justify-center">
                                                <svg viewBox="0 0 24 24" width="12" height="12" className="text-emerald-300" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="m9 12 2 2 4-4" />
                                                </svg>
                                            </span>
                                            <span className="text-sm font-medium text-emerald-200">No shadows</span>
                                        </li>
                                        <li className="flex items-center gap-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20 px-3 py-2">
                                            <span className="h-5 w-5 rounded-full bg-emerald-500/30 flex items-center justify-center">
                                                <svg viewBox="0 0 24 24" width="12" height="12" className="text-emerald-300" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="m9 12 2 2 4-4" />
                                                </svg>
                                            </span>
                                            <span className="text-sm font-medium text-emerald-200">Flat page</span>
                                        </li>
                                        <li className="flex items-center gap-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20 px-3 py-2">
                                            <span className="h-5 w-5 rounded-full bg-emerald-500/30 flex items-center justify-center">
                                                <svg viewBox="0 0 24 24" width="12" height="12" className="text-emerald-300" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="m9 12 2 2 4-4" />
                                                </svg>
                                            </span>
                                            <span className="text-sm font-medium text-emerald-200">High contrast ink</span>
                                        </li>
                                    </ul>

                                    <div className="flex items-start gap-3 rounded-lg bg-slate-800/40 border border-white/10 p-4">
                                        <div className="h-8 w-8 rounded-lg bg-slate-700/70 border border-white/10 flex items-center justify-center flex-shrink-0">
                                            <svg viewBox="0 0 24 24" width="16" height="16" className="text-slate-300" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <circle cx="12" cy="12" r="10" />
                                                <line x1="12" y1="16" x2="12" y2="12" />
                                                <line x1="12" y1="8" x2="12.01" y2="8" />
                                            </svg>
                                        </div>
                                        <p className="text-sm text-slate-300 leading-relaxed">We extract terms, definitions, and key concepts. Review and edit before saving to a folder.</p>
                                    </div>
                                </div>

                                {/* Removed FlashFoto-specific Review Queue in favor of unified queue */}

                                {/* Mobile Review Queue - between controls and folders (FlashFoto) */}
                                {renderMobileQueue('mt-6')}

                                {/* FlashFoto Folders Section (span full width on desktop) */}
                                {renderAppHomeFoldersSection({ sectionClassName: 'lg:col-span-2' })}
                            </div>
                        ) : productMode === 'clinical' ? (
                            <div>
                            {(!user || user.isAnonymous) && !(entitlements?.tier && entitlements.tier !== 'free') && (
                                <FreeTrialBanner
                                    questionsUsed={freeQuestionsUsed}
                                    onUpgrade={() => onOpenPlanChooser?.()}
                                    onSignUp={() => onOpenAccount?.()}
                                    isAnonymous={!user || user.isAnonymous}
                                />
                            )}
                            <FlashClinicalWorkspace
                                isDevMode={isDevMode}
                                isOnline={isOnline}
                                isSafari={isSafari}
                                homeUsage={homeUsage}
                                entitlements={entitlements}
                                remaining={remaining}
                                clinicalInputType={clinicalInputType}
                                onClinicalInputTypeChange={setClinicalInputType}
                                clinicalSettings={clinicalSettings}
                                onClinicalSettingsChange={setClinicalSettings}
                                clinicalNotes={clinicalNotes}
                                onClinicalNotesChange={setClinicalNotes}
                                onClinicalNotesPaste={handleClinicalNotesPaste}
                                clinicalPastedImagePreviewUrl={clinicalPastedImagePreviewUrl}
                                onRemoveClinicalPastedImage={clearClinicalPastedImage}
                                onClinicalBatchGenerate={handleClinicalBatchGenerate}
                                isClinicalGenerating={isClinicalGenerating}
                                isGenerating={isGenerating}
                                generateClinicalFlashcardRequest={generateClinicalFlashcardRequest}
                                flashFonicNotify={flashFonicNotify}
                                upload={upload}
                                onOpenSettings={() => setShowSettingsModal(true)}
                                clinicalSettingsForPayload={clinicalSettings}
                                targetFolderId={targetFolderId}
                                onTargetFolderChange={setTargetFolderId}
                                folders={folders}
                                onCreateFolder={() => setIsCreateOpen(true)}
                                isMovingQueueCards={isMovingQueueCards}
                                imagePreview={imagePreview}
                                documentFileName={documentFileName}
                                dragActive={dragActive}
                                isCameraOn={isCameraOn}
                                cameraErr={cameraErr}
                                fileInputRef={fileInputRef}
                                videoRef={videoRef}
                                canvasRef={canvasRef}
                                onDrop={onDrop}
                                onDragEnter={(e) => {
                                    e.preventDefault();
                                    setDragActive(true);
                                }}
                                onDragLeave={(e) => {
                                    e.preventDefault();
                                    setDragActive(false);
                                }}
                                onFileChange={handleFileChange}
                                openFilePicker={openFilePicker}
                                startCamera={startCamera}
                                stopCamera={stopCamera}
                                takePicture={takePicture}
                                statusMsg={statusMsg}
                                onImageCropReady={onImageCropReady}
                                onSelectionError={onSelectionError}
                                generatedCards={renderClinicalGeneratedCards()}
                                renderFoldersSection={renderAppHomeFoldersSection}
                            />
                            </div>
                        ) : (
                            <div className="mt-8">
                                <FlashFonicProvider
                                    isDevMode={isDevMode}
                                    usage={homeUsage}
                                    duration={Number(localStorage.getItem('ff:listenMinutes') || '0')}
                                    isSafari={isSafari}
                                    setNotification={flashFonicNotify}
                                    generateFlashcardRequest={generateFlashcardRequestLive}
                                >
                                    <FlashFonicPanel />
                                </FlashFonicProvider>
                            </div>
                        )}

                        {/* Folder Preview Dialog */}
                        {previewFolder && (
                            <Modal title=" " size="3xl" onClose={() => setPreviewFolder(null)} bodyClassName="p-0">
                                {/* Decorative header with centered title and count */}
                                <div className="relative">
                                    <div className="relative h-28">
                                        <img alt="" src="https://images.unsplash.com/photo-1621619856624-42fd193a0661?w=1080&q=80" className="absolute inset-0 h-full w-full object-cover opacity-30" />
                                        <div className="absolute inset-0 bg-gradient-to-b from-slate-900/70 to-slate-950"></div>
                                        <div className="absolute inset-x-0 top-2 flex justify-center lg:hidden">
                                            <span className="h-1.5 w-12 rounded-full bg-white/30"></span>
                                        </div>
                                        <div className="absolute inset-x-0 top-0 flex items-center justify-between px-4 pt-3">
                                            <div />
                                            <div className="text-center">
                                                <h1 className="text-[22px] sm:text-2xl font-semibold tracking-tight text-white">{previewFolder.name}</h1>
                                                <p className="mt-0.5 text-xs text-slate-300/80">Study set • {(previewFolder.cards || []).length} cards</p>
                                            </div>
                                            <div />
                                        </div>
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 lg:grid-cols-3 max-h-[80vh]">
                                    {/* Mobile primary actions: fit on screen (2x2 grid, no scroll) */}
                                    <div className="lg:hidden border-b border-white/10">
                                        <div className="px-3 py-2">
                                            <div className="grid grid-cols-2 gap-2">
                                                <button onClick={() => { setPreviewFolder(null); openStudy(previewFolder); }} className="inline-flex items-center justify-center rounded-full px-3.5 py-2 text-[13px] text-white bg-gradient-to-r from-emerald-600 to-teal-600">Study</button>
                                                <button onClick={() => { setPreviewFolder(null); setFlashNotesAction({ folder: previewFolder }); }} className="inline-flex items-center justify-center rounded-full px-3.5 py-2 text-[13px] text-white bg-gradient-to-r from-rose-600 to-pink-600 disabled:opacity-60">Flash Notes</button>
                                                <button onClick={() => {
                                                    const isPaidTier = entitlements?.tier && entitlements.tier !== 'free';
                                                    if (isDevMode || isPaidTier || (entitlements?.includedGamesRemaining ?? 0) > 0) { setPreviewFolder(null); handlePlayGame(previewFolder); return; }
                                                    // Free tier with no included games remaining — prompt for credits
                                                    setPreviewFolder(null);
                                                    setCreditPrompt({ type: 'games', folder: previewFolder });
                                                }} className="inline-flex items-center justify-center rounded-full px-3.5 py-2 text-[13px] text-white bg-gradient-to-r from-teal-600 to-emerald-600">Games</button>
                                                <button onClick={() => { setExamPreSelectedFolderId(previewFolder.id); setPreviewFolder(null); setShowExamCreate(true); }} className="inline-flex items-center justify-center rounded-full px-3.5 py-2 text-[13px] text-slate-900 bg-amber-300">Flash Exam</button>
                                            </div>
                                        </div>
                                    </div>
                                    {/* Left: Clean actions panel */}
                                    <div className="hidden lg:block p-4 border-b lg:border-b-0 lg:border-r border-white/10 lg:sticky lg:top-0 lg:self-start lg:h-[80vh] overflow-auto">
                                        <div className="flex items-center gap-3 mb-3">
                                            <div className={`h-8 w-8 rounded-lg bg-gradient-to-br ${previewFolder.color || 'from-teal-600 to-emerald-600'} ring-1 ring-white/10 flex items-center justify-center`}>
                                                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 7h5l2 3h11v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /></svg>
                                            </div>
                                            <div className="text-xs text-slate-400">{(previewFolder.cards || []).length} cards</div>
                                        </div>
                                        {/* Hide initial Move UI; use sticky toolbar or mobile bar once selection exists */}
                                        {hasMoreFolders && (
                                            <div className="mt-5 flex justify-center">
                                                <button
                                                    type="button"
                                                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); setPage(p => p + 1); }}
                                                    className="inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm text-slate-200 bg-slate-800/70 hover:bg-slate-800 ring-1 ring-white/10"
                                                >
                                                    Load more
                                                </button>
                                            </div>
                                        )}
                                        <div className="grid gap-2">
                                            <button onClick={() => { setPreviewFolder(null); openStudy(previewFolder); }} className="w-full inline-flex items-center justify-between rounded-xl px-3.5 py-2.5 bg-slate-800/60 hover:bg-slate-800 ring-1 ring-white/10 text-slate-200">
                                                <span className="inline-flex items-center gap-2"><span className="h-2 w-2 rounded-full bg-emerald-400"></span> Study</span>
                                                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor"><path d="M9 18l6-6-6-6" /></svg>
                                            </button>
                                            <button onClick={() => { setPreviewFolder(null); setFlashNotesAction({ folder: previewFolder }); }} className={`w-full inline-flex items-center justify-between rounded-xl px-3.5 py-2.5 ring-1 ring-white/10 ${(!isDevMode && (entitlements?.includedNotesRemaining ?? 0) <= 0 && (entitlements?.creditsBalance ?? 0) <= 0) ? 'bg-slate-800/40 text-slate-500 cursor-not-allowed' : 'bg-slate-800/60 hover:bg-slate-800 text-slate-200'}`}>
                                                <span className="inline-flex items-center gap-2"><span className="h-2 w-2 rounded-full bg-rose-400"></span> Flash Notes</span>
                                                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor"><path d="M9 18l6-6-6-6" /></svg>
                                            </button>
                                            <button onClick={() => {
                                                const isPaidTier = entitlements?.tier && entitlements.tier !== 'free';
                                                if (isDevMode || isPaidTier || (entitlements?.includedGamesRemaining ?? 0) > 0) { setPreviewFolder(null); handlePlayGame(previewFolder); return; }
                                                setPreviewFolder(null);
                                                setCreditPrompt({ type: 'games', folder: previewFolder });
                                            }} className={`w-full inline-flex items-center justify-between rounded-xl px-3.5 py-2.5 ring-1 ring-white/10 ${(!isDevMode && !(entitlements?.tier && entitlements.tier !== 'free') && (entitlements?.includedGamesRemaining ?? 0) <= 0) ? 'bg-slate-800/40 text-slate-500' : 'bg-slate-800/60 hover:bg-slate-800 text-slate-200'}`}>
                                                <span className="inline-flex items-center gap-2"><span className="h-2 w-2 rounded-full bg-teal-400"></span> Games</span>
                                                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor"><path d="M9 18l6-6-6-6" /></svg>
                                            </button>
                                            <button onClick={() => {
                                                setExamPreSelectedFolderId(previewFolder?.id ?? null);
                                                setPreviewFolder(null);
                                                setShowExamCreate(true);
                                            }} className="w-full inline-flex items-center justify-between rounded-xl px-3.5 py-2.5 ring-1 ring-white/10 bg-slate-800/60 hover:bg-slate-800 text-slate-200">
                                                <span className="inline-flex items-center gap-2"><span className="h-2 w-2 rounded-full bg-amber-400"></span> Flash Exam</span>
                                                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor"><path d="M9 18l6-6-6-6" /></svg>
                                            </button>
                                        </div>
                                        <div className="mt-4">
                                            <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">Exports</div>
                                            <div className="grid gap-1">
                                                <button className="w-full text-left px-3 py-2 rounded-lg text-sm hover:bg-white/5" onClick={() => { exportFolderToPDF(previewFolder); }}>Export PDF</button>
                                                <button className="w-full text-left px-3 py-2 rounded-lg text-sm hover:bg-white/5" onClick={() => { exportFolderToCSV(previewFolder); }}>Export CSV</button>
                                                <button disabled={isExportingAnki} className="w-full text-left px-3 py-2 rounded-lg text-sm hover:bg-white/5 disabled:opacity-50" onClick={() => { exportFolderToAnkiFile(previewFolder); }}>{isExportingAnki ? 'Exporting…' : 'Export Anki (.apkg)'}</button>
                                            </div>
                                            <div className="mt-3">
                                                <button type="button" onClick={() => { setDeletePrompt({ id: previewFolder.id, name: previewFolder.name }); }} className="w-full inline-flex items-center justify-between rounded-xl px-3.5 py-2.5 bg-rose-600/80 hover:bg-rose-600 text-white">
                                                    <span className="inline-flex items-center gap-2">Delete Folder</span>
                                                    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor"><path d="M3 6h18" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /></svg>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="lg:col-span-2 px-4 pt-0 pb-28 max-h-[80vh] overflow-auto min-h-0 lg:pb-0">
                                        {/* Batch selection bar with filters and quick actions */}
                                        <div className="sticky -top-1 z-10 -mx-4 px-4 py-2 bg-slate-900/80 backdrop-blur border-b border-white/10 shadow-lg shadow-black/40 grid gap-2">
                                            <div className="flex flex-wrap items-center justify-between gap-2">
                                                <div className="flex items-center gap-3 text-xs text-slate-300">
                                                    <label className="inline-flex items-center gap-2">
                                                        <input
                                                            type="checkbox"
                                                            checked={Boolean(previewFolder?.cards?.length) && (previewFolder.cards || []).every(c => !!previewSelected[c.id])}
                                                            onChange={(e) => {
                                                                const checked = e.target.checked;
                                                                setPreviewSelected(prev => {
                                                                    const next = { ...prev };
                                                                    (previewFolder.cards || []).forEach(c => { next[c.id] = checked; });
                                                                    return next;
                                                                });
                                                            }}
                                                        />
                                                        Select all
                                                    </label>
                                                    <span>Selected: {Object.values(previewSelected).filter(Boolean).length}</span>
                                                    {/* Remove always-visible Move dropdown; keep quick action to set last used destination if desired */}
                                                </div>
                                                {/* Hide move controls here; they appear when selection exists (mobile bottom bar) */}
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <label className="flex min-w-0 items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-3 py-1.5">
                                                    <svg viewBox="0 0 24 24" width="16" height="16" className="text-slate-300/80"><circle cx="11" cy="11" r="8" stroke="currentColor" fill="none" /><path d="M21 21l-3.5-3.5" stroke="currentColor" /></svg>
                                                    <input className="w-full min-w-0 bg-transparent text-xs placeholder:text-slate-400 focus:outline-none text-slate-200" placeholder="Search in folder" value={previewSearch} onChange={(e) => setPreviewSearch(e.target.value)} />
                                                    {previewSearch && (
                                                        <button type="button" className="rounded-md px-1.5 py-0.5 text-slate-300 hover:bg-white/10" onClick={() => setPreviewSearch('')}>
                                                            <svg viewBox="0 0 24 24" width="16" height="16" className="text-slate-300"><path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" /></svg>
                                                        </button>
                                                    )}
                                                </label>
                                                <div className="inline-flex rounded-xl bg-slate-800/70 p-1 border border-white/10">
                                                    <button className={`px-3 py-1.5 rounded-lg text-xs ${previewFilter === 'all' ? 'bg-slate-700/60 text-white' : 'text-slate-300'}`} onClick={() => setPreviewFilter('all')}>All</button>
                                                    <button className={`px-3 py-1.5 rounded-lg text-xs ${previewFilter === 'flagged' ? 'bg-slate-700/60 text-white' : 'text-slate-300'}`} onClick={() => setPreviewFilter('flagged')}>Flagged</button>
                                                </div>
                                            </div>

                                            {/* Desktop action controls: show when there is a selection */}
                                            {Object.values(previewSelected).some(Boolean) && (
                                                <div className="hidden lg:flex items-center justify-between gap-3">
                                                    <div className="text-xs text-slate-300">Actions for {Object.values(previewSelected).filter(Boolean).length} selected</div>
                                                    <div className="flex items-center gap-2">
                                                        <select className="rounded-xl bg-slate-800/70 border border-white/10 px-3.5 py-1.5 text-xs text-slate-200" value={previewMoveDestId} onChange={(e) => setPreviewMoveDestId(e.target.value)}>
                                                            <option value="">Move to…</option>
                                                            {folders.filter(f => f.id !== previewFolder.id).map(f => (<option key={f.id} value={f.id}>{f.name}</option>))}
                                                        </select>
                                                        <button type="button" onClick={moveSelectedCardsFromPreview} disabled={!previewMoveDestId} className="inline-flex items-center gap-2 rounded-xl px-3 py-1.5 text-xs text-white bg-gradient-to-r from-teal-600 to-emerald-600 disabled:opacity-60">Move</button>
                                                        <button type="button" onClick={deleteSelectedCardsFromPreview} className="inline-flex items-center gap-2 rounded-xl px-3 py-1.5 text-xs text-rose-200 bg-rose-600/20 ring-1 ring-rose-500/30 hover:bg-rose-600/30">Delete</button>
                                                    </div>
                                                </div>
                                            )}
                                            {(lastMove || moveError) && (
                                                <div className="-mx-4 px-4 py-2 bg-slate-800/60 border-y border-white/10 flex items-center justify-between text-xs">
                                                    <span className="text-slate-300">{moveError ? moveError : 'Cards moved.'}</span>
                                                    <div className="flex items-center gap-2">
                                                        {lastMove && (<button className="text-teal-300 hover:text-teal-200" onClick={undoLastMove}>Undo</button>)}
                                                        <button className="text-slate-400 hover:text-slate-300" onClick={() => { setLastMove(null); setMoveError(null); }}>Dismiss</button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                        {(() => {
                                            const all = previewFolder.cards || [];
                                            const filtered = all.filter(c => {
                                                if (previewFilter === 'flagged' && !c.isFlagged) return false;
                                                if (!previewSearch) return true;
                                                const q = String(c.question || '').toLowerCase();
                                                const a = String(c.answer || '').toLowerCase();
                                                const s = previewSearch.toLowerCase();
                                                return q.includes(s) || a.includes(s);
                                            });
                                            return filtered.length ? (
                                                <div className="space-y-2">
                                                    {filtered.map((c) => (
                                                        <div key={c.id} className="rounded-xl bg-slate-950/70 ring-1 ring-white/10 p-2 sm:p-3 flex items-start gap-3">
                                                            {previewEditingCardId === c.id ? (
                                                                <div>
                                                                    <EditableCardContent
                                                                        card={c}
                                                                        onSave={savePreviewCardEdit}
                                                                        onCancel={() => setPreviewEditingCardId(null)}
                                                                    />
                                                                </div>
                                                            ) : (
                                                                <div className="flex items-start gap-3">
                                                                    <input type="checkbox" className="mt-0.5" checked={!!previewSelected[c.id]} onChange={(e) => setPreviewSelected(prev => ({ ...prev, [c.id]: e.target.checked }))} />
                                                                    <div className="flex-1">
                                                                        <p className="text-slate-200 text-sm leading-snug"><strong>Q:</strong> {typeof c.question === 'string' ? c.question : JSON.stringify(c.question)}</p>
                                                                        <p className="text-slate-300 mt-1 text-sm leading-snug"><strong>A:</strong> {typeof c.answer === 'string' ? c.answer : JSON.stringify(c.answer)}</p>
                                                                    </div>
                                                                    <div className="flex items-center gap-1">
                                                                        <button type="button" title="Edit" className="rounded-md p-1 text-slate-300 hover:bg-white/10" onClick={() => setPreviewEditingCardId(c.id)}>
                                                                            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
                                                                        </button>
                                                                        <button type="button" title="Flag" className={`rounded-md p-1 ${c.isFlagged ? 'text-amber-300 hover:bg-amber-400/10' : 'text-slate-300 hover:bg-white/10'}`} onClick={async () => {
                                                                            try {
                                                                                const db = getDb();
                                                                                const currentUid = getCurrentUid();
                                                                                if (!db || !currentUid) return;
                                                                                const next = !c.isFlagged;
                                                                                await updateDoc(doc(db, 'users', currentUid, 'folders', previewFolder.id, 'cards', String(c.id)), { isFlagged: next });
                                                                                setPreviewFolder(prev => prev ? { ...prev, cards: (prev.cards || []).map(x => x.id === c.id ? { ...x, isFlagged: next } : x) } : prev);
                                                                                setFolders(curr => curr.map(f => f.id === previewFolder.id ? { ...f, cards: (f.cards || []).map(x => x.id === c.id ? { ...x, isFlagged: next } : x) } : f));
                                                                            } catch (_) { }
                                                                        }}>
                                                                            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16v12l-6-3-6 3V4z" /></svg>
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <p className="text-sm text-slate-500">No matching cards.</p>
                                            );
                                        })()}

                                        {/* Mobile bottom action bar */}
                                        <div className={`lg:hidden fixed inset-x-0 bottom-0 z-50 mx-auto w-full max-w-3xl px-3 transition ${Object.values(previewSelected).some(Boolean) ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}>
                                            <div className="mx-1 mb-4 rounded-xl border border-white/10 bg-slate-900/95 shadow-xl ring-1 ring-white/10 backdrop-blur">
                                                <div className="flex items-center gap-2 p-3">
                                                    <div className="flex items-center gap-2 rounded-lg bg-white/5 px-2 py-1.5">
                                                        <svg viewBox="0 0 24 24" width="16" height="16" className="text-slate-300"><path d="M3 7h5l2 3h11v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" stroke="currentColor" fill="none" /></svg>
                                                        <button type="button" onClick={() => setMobileMoveMenuOpen(v => !v)} className="rounded-md px-2 py-1 text-[13px] font-medium text-slate-100 hover:bg-white/10">Move</button>
                                                    </div>
                                                    <button type="button" onClick={duplicateSelectedCardsInPreview} className="rounded-lg px-3 py-1.5 text-[13px] text-slate-300 hover:bg-white/10">
                                                        <span className="inline-flex items-center gap-1"><svg viewBox="0 0 24 24" width="16" height="16" className="text-slate-300"><rect x="9" y="9" width="13" height="13" rx="2" ry="2" stroke="currentColor" fill="none" /><rect x="3" y="3" width="13" height="13" rx="2" ry="2" stroke="currentColor" fill="none" /></svg> Duplicate</span>
                                                    </button>
                                                    <button type="button" onClick={deleteSelectedCardsFromPreview} className="ml-auto rounded-lg bg-rose-500/15 px-3 py-1.5 text-[13px] font-medium text-rose-300 ring-1 ring-rose-400/30 hover:bg-rose-500/25">
                                                        <span className="inline-flex items-center gap-1"><svg viewBox="0 0 24 24" width="16" height="16" className="text-rose-300"><path d="M3 6h18" stroke="currentColor" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" stroke="currentColor" /><path d="M10 11v6" stroke="currentColor" /><path d="M14 11v6" stroke="currentColor" /></svg> Delete</span>
                                                    </button>
                                                </div>
                                            </div>
                                            {mobileMoveMenuOpen && (
                                                <div className="mx-1 -mt-2 mb-6 overflow-hidden rounded-xl border border-white/10 bg-slate-900/95 shadow-xl ring-1 ring-white/10 backdrop-blur">
                                                    <div className="px-3 pb-2 pt-3 text-[12px] text-slate-300">Move selected to</div>
                                                    {folders.filter(f => f.id !== previewFolder.id).slice(0, 6).map(f => (
                                                        <button key={f.id} onClick={() => { setPreviewMoveDestId(f.id); setMobileMoveMenuOpen(false); setTimeout(() => moveSelectedCardsFromPreview(), 0); }} className="flex w-full items-center justify-between px-3 py-2 text-left text-[14px] text-slate-200 hover:bg-white/5">
                                                            <span className="inline-flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full bg-white/20"></span> {f.name}</span>
                                                            <svg viewBox="0 0 24 24" width="16" height="16" className="text-slate-400"><path d="M9 18l6-6-6-6" stroke="currentColor" fill="none" /></svg>
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </Modal>
                        )}

                    </section>

                    {/* Best results tips moved to the end for minimal distraction */}
                    <section className="mt-12 px-2 sm:px-4 md:px-6">
                        <div className="rounded-3xl border border-white/10 bg-gradient-to-br from-slate-900/60 via-slate-900/80 to-slate-950/90 p-6 sm:p-8 shadow-2xl shadow-black/30">
                            <div className="flex items-start gap-4 mb-6">
                                <div className="h-12 w-12 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg shadow-emerald-500/20">
                                    <svg viewBox="0 0 24 24" width="20" height="20" className="text-white" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 11H5a2 2 0 0 0-2 2v3c0 1.1.9 2 2 2h2" /><path d="M20 12a9 9 0 1 1-6.18-8.49" /><path d="M9 12h4l-2 2" /><path d="M11 10l-2-2" /></svg>
                                </div>
                                <div>
                                    <h4 className="text-xl font-semibold tracking-tight text-white">Best results</h4>
                                    <p className="mt-1 text-sm text-slate-400">Follow these tips for optimal flashcard generation</p>
                                </div>
                            </div>
                            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
                                <li className="flex items-center gap-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20 px-3 py-2"><span className="h-5 w-5 rounded-full bg-emerald-500/30 flex items-center justify-center"><svg viewBox="0 0 24 24" width="12" height="12" className="text-emerald-300" fill="none" stroke="currentColor" strokeWidth="2"><path d="m9 12 2 2 4-4" /></svg></span><span className="text-sm font-medium text-emerald-200">Good lighting</span></li>
                                <li className="flex items-center gap-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20 px-3 py-2"><span className="h-5 w-5 rounded-full bg-emerald-500/30 flex items-center justify-center"><svg viewBox="0 0 24 24" width="12" height="12" className="text-emerald-300" fill="none" stroke="currentColor" strokeWidth="2"><path d="m9 12 2 2 4-4" /></svg></span><span className="text-sm font-medium text-emerald-200">No shadows</span></li>
                                <li className="flex items-center gap-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20 px-3 py-2"><span className="h-5 w-5 rounded-full bg-emerald-500/30 flex items-center justify-center"><svg viewBox="0 0 24 24" width="12" height="12" className="text-emerald-300" fill="none" stroke="currentColor" strokeWidth="2"><path d="m9 12 2 2 4-4" /></svg></span><span className="text-sm font-medium text-emerald-200">Flat page</span></li>
                                <li className="flex items-center gap-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20 px-3 py-2"><span className="h-5 w-5 rounded-full bg-emerald-500/30 flex items-center justify-center"><svg viewBox="0 0 24 24" width="12" height="12" className="text-emerald-300" fill="none" stroke="currentColor" strokeWidth="2"><path d="m9 12 2 2 4-4" /></svg></span><span className="text-sm font-medium text-emerald-200">High contrast ink</span></li>
                            </ul>
                            <div className="flex items-start gap-3 rounded-lg bg-slate-800/40 border border-white/10 p-4">
                                <div className="h-8 w-8 rounded-lg bg-slate-700/70 border border-white/10 flex items-center justify-center flex-shrink-0">
                                    <svg viewBox="0 0 24 24" width="16" height="16" className="text-slate-300" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" /></svg>
                                </div>
                                <p className="text-sm text-slate-300 leading-relaxed">We extract terms, definitions, and key concepts. Review and edit before saving to a folder.</p>
                            </div>
                        </div>
                    </section>
                </main>

                {/* Footer (keep existing footer component for legal links wiring) */}
                <footer className="mt-auto">
                    <div className="mx-auto w-full max-w-[1600px] px-2 sm:px-3 lg:px-4 py-8">
                        <div className="flex flex-col items-center gap-4">
                            <div className="flex flex-wrap items-center justify-center gap-3">
                                <button type="button" className="inline-flex items-center rounded-lg px-4 py-2 text-sm font-medium ring-1 ring-white/10 bg-transparent hover:bg-white/5 text-slate-200" onClick={() => setIsFeedbackModalOpen(true)}>Contact Us</button>
                                <button type="button" className="inline-flex items-center rounded-lg px-4 py-2 text-sm font-medium ring-1 ring-white/10 bg-transparent hover:bg-white/5 text-slate-200" onClick={() => navigate('/terms')}>Terms and Conditions</button>
                                <button type="button" className="inline-flex items-center rounded-lg px-4 py-2 text-sm font-medium ring-1 ring-white/10 bg-transparent hover:bg-white/5 text-slate-200" onClick={() => navigate('/privacy')}>Privacy Policy</button>
                            </div>
                            <p className="text-xs text-slate-500">© FlashFonic, Trifecta Pro LLC</p>
                        </div>
                    </div>
                </footer>
            </div>

            {/* Modals */}
            {showGuestSignupPrompt && (
                <Modal title="Enjoying FlashFonic?" onClose={() => setShowGuestSignupPrompt(false)}>
                    <div className="space-y-3">
                        <p className="text-sm text-slate-300">Sign up for a free account to save your folders and progress.</p>
                        <div className="flex justify-end gap-2">
                            <button className="inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm text-slate-200 bg-slate-800/70 hover:bg-slate-800 ring-1 ring-white/10" onClick={() => setShowGuestSignupPrompt(false)}>Maybe later</button>
                            <button className="inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm text-white bg-gradient-to-r from-teal-600 to-emerald-600" onClick={() => { setShowGuestSignupPrompt(false); onOpenAccount && onOpenAccount(); }}>Sign up for free</button>
                        </div>
                    </div>
                </Modal>
            )}
            {isCreateOpen && (
                <CreateFolderModal
                    onClose={() => setIsCreateOpen(false)}
                    onCreate={handleCreate}
                    title="Create Folder"
                />
            )}
            {duplicatePrompt && (
                <ConfirmModal
                    message={`A folder named "${duplicatePrompt.name}" already exists. Add to existing instead?`}
                    onClose={() => setDuplicatePrompt(null)}
                    onConfirm={() => {
                        // Set targetFolderId to the existing folder and close
                        const existing = folders.find(f => (f.name || '').trim().toLowerCase() === duplicatePrompt.name.trim().toLowerCase());
                        if (existing) setTargetFolderId(existing.id);
                        setDuplicatePrompt(null);
                        setIsCreateOpen(false);
                    }}
                />
            )}
            {creditPrompt && (
                <CreditConversionModal
                    type={creditPrompt.type}
                    folder={creditPrompt.folder}
                    creditsBalance={entitlements?.creditsBalance ?? 0}
                    defaultQuestions={10}
                    onClose={() => setCreditPrompt(null)}
                    onConfirm={async ({ cost, count, selectedQuestions }) => {
                        // For exams: do not debit here; only set max questions and proceed to folder selection. The credit debit happens server-side inside POST /api/exams.
                        if (creditPrompt.type === 'exam') {
                            setMaxExamQuestions(Number(selectedQuestions || 0));
                            setCreditPrompt(null);
                            setExamWizardState({ stage: 'folder_selection' });
                            return;
                        }
                        // Non-exam: prior behavior
                        try {
                            let r;
                            if (creditPrompt.type === 'games') {
                                r = await authApi(`${API_BASE_URL}/api/credits/purchase-included`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ feature: 'game', units: cost }) });
                                if (typeof refreshEntitlements === 'function') { try { await refreshEntitlements(); } catch (_) { } }
                                setCreditPrompt(null);
                                handlePlayGame(creditPrompt.folder);
                                return;
                            } else if (creditPrompt.type === 'notes') {
                                // Credits are debited atomically server-side inside /generate-notes
                                // (included-notes allowance first, then creditsBalance) and refunded
                                // in full if generation fails — no client-side pre-debit anymore.
                                const resumeFolder = creditPrompt.folder;
                                const resumeAction = creditPrompt.action || 'view';
                                const resumeRegenerate = Boolean(creditPrompt.forceRegenerate);
                                setCreditPrompt(null);
                                await handleGenerateNotes(resumeFolder, resumeAction, resumeRegenerate, true);
                                return;
                            } else {
                                r = await authApi(`${API_BASE_URL}/api/credits/debit`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ feature: 'flashcredit', quantity: cost }) });
                                if (typeof refreshEntitlements === 'function') { try { await refreshEntitlements(); } catch (_) { } }
                            }
                        } catch (e) {
                            console.error('[AppHome] credit prompt debit failed:', { code: e?.code, requestId: e?.requestId, status: e?.status });
                            pushToast('error', e?.message || 'Payment failed');
                        }
                        setCreditPrompt(null);
                        if (creditPrompt.type !== 'games' && creditPrompt.type !== 'notes') {
                            // No specific resume action
                        }
                    }}
                />
            )}


            {deletePrompt && (
                <ConfirmModal
                    message={`Delete folder "${deletePrompt.name}"? This will remove the folder and all of its cards.`}
                    onClose={() => setDeletePrompt(null)}
                    onConfirm={() => {
                        const id = deletePrompt.id;
                        setDeletePrompt(null);
                        setPreviewFolder(null);
                        deleteFolderAndCards(id);
                        pushToast('success', 'Folder deleted');
                    }}
                />
            )}

            {showFotoAIModal && (
                <Modal title="Card Count Suggestion" onClose={() => setShowFotoAIModal(false)}>
                    <div className="space-y-3">
                        <p className="text-slate-300">Suggested: <span className="text-teal-300 font-semibold">{fotoAIRec}</span> flashcards for this photo.</p>
                        <div className="flex items-center gap-3">
                            <button
                                type="button"
                                onClick={() => { setShowFotoAIModal(false); handleAutoGenerate(fotoAIRec || cardCount); }}
                                className="inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm text-white bg-gradient-to-r from-teal-600 to-emerald-600"
                            >
                                Use Suggestion
                            </button>
                            <button
                                type="button"
                                onClick={() => { setShowFotoAIModal(false); handleAutoGenerate(cardCount); }}
                                className="inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm text-slate-200 bg-slate-800/70 ring-1 ring-white/10"
                            >
                                Use my slider value ({cardCount})
                            </button>
                        </div>
                    </div>
                </Modal>
            )}
            {renameId && (
                <EnterNameModal
                    onClose={() => setRenameId(null)}
                    onConfirm={(name) => handleRename(renameId, name)}
                />
            )}
            {pendingRename && (
                <EnterNameModal
                    onClose={() => setPendingRename(null)}
                    onConfirm={(name) => { handleRename(pendingRename.id, name); setPendingRename(null); }}
                />
            )}
            {studyingFolder && (
                <FlashcardViewer
                    folder={studyingFolder}
                    entitlements={entitlements}
                    isDevMode={isDevMode}
                    onOpenCredits={(payload) => setCreditPrompt(payload)}
                    onClose={(finalDeck) => {
                        const studied = Array.isArray(finalDeck) ? finalDeck.filter(c => c.lastViewed).length : 0;
                        if (studied > 0) { addXP(studied * XP_PER_CARD); recordStudyToday(); }
                        setStudyingFolder(null);
                        if (lastPreviewFolderRef.current) { setPreviewFolder(lastPreviewFolderRef.current); }
                    }}
                    onLaunchGame={(folder) => { setStudyingFolder(null); handleLaunchGame(folder); }}
                    onLaunchAnamnesisNemesis={(folder) => { setStudyingFolder(null); handleLaunchNemesis(folder || studyingFolder); }}
                    onLaunchExam={(folder) => { setStudyingFolder(null); handleQuickTest(folder); }}
                    onCardRated={async (updatedCard) => {
                        try {
                            setFolders(prev => prev.map(f => f.id === studyingFolder.id
                                ? { ...f, cards: (f.cards || []).map(c => c.id === updatedCard.id ? { ...c, ...updatedCard } : c) }
                                : f
                            ));
                            const db = getDb(); const uid = getCurrentUid();
                            if (db && uid && studyingFolder.id) {
                                const { doc: fsDoc, updateDoc: fsUpdate } = await import('firebase/firestore');
                                await fsUpdate(fsDoc(db, 'users', uid, 'folders', studyingFolder.id, 'cards', String(updatedCard.id)), {
                                    srsInterval: updatedCard.srsInterval,
                                    srsEaseFactor: updatedCard.srsEaseFactor,
                                    srsRepetitions: updatedCard.srsRepetitions,
                                    srsDueDate: updatedCard.srsDueDate,
                                });
                            }
                        } catch (_) {}
                    }}
                />
            )}
            {showGamesModal && (
                <GamesModal
                    folder={showGamesModal}
                    onClose={() => setShowGamesModal(null)}
                    onLaunchGame={(folder) => handleLaunchGame(folder)}
                    onLaunchAnamnesisNemesis={(folder) => { setShowGamesModal(null); handleLaunchNemesis(folder); }}
                />
            )}
            {gameModeFolder && (
                <GameViewer
                    key={gameModeFolder.id}
                    folder={gameModeFolder}
                    onClose={handleGameEnd}
                    onBackToStudy={(folder) => { setGameModeFolder(null); setStudyingFolder(folder); }}
                    onExitGame={() => setGameModeFolder(null)}
                    cameFromStudy={false}
                    mostRecentScore={mostRecentScore}
                />
            )}
            {nemesisModeFolder && (
                <NemesisGame
                    folder={nemesisModeFolder}
                    onClose={() => setNemesisModeFolder(null)}
                />
            )}
            {flashNotesAction && (
                <FlashNotesActionModal
                    folder={flashNotesAction.folder}
                    onClose={() => setFlashNotesAction(null)}
                    onGenerate={handleGenerateNotes}
                    isGenerating={isGeneratingNotes}
                />
            )}
            {showFlashNotesViewer && flashNotesContent && (
                <FlashNotesViewer
                    folderName={flashNotesContent.folderName}
                    notes={flashNotesContent.notes}
                    onSave={async (editedNotes) => {
                        const fid = flashNotesContent.folderId;
                        if (!fid) return;
                        setFolders(prev => prev.map(f => f.id === fid ? { ...f, flashNotes: editedNotes } : f));
                        setFlashNotesContent(prev => ({ ...prev, notes: editedNotes }));
                        try {
                            const db = getDb(); const uid = getCurrentUid();
                            if (db && uid) await updateDoc(doc(db, 'users', uid, 'folders', fid), { flashNotes: editedNotes, updatedAt: serverTimestamp() });
                        } catch (_) {}
                        pushToast('success', 'Notes saved');
                    }}
                    onClose={() => {
                        setShowFlashNotesViewer(false);
                        if (lastPreviewFolderRef.current) {
                            setPreviewFolder(lastPreviewFolderRef.current);
                            lastPreviewFolderRef.current = null;
                        }
                    }}
                />
            )}
            {activeExam && (
                <ExamViewer
                    exam={activeExam}
                    onClose={() => setActiveExam(null)}
                    onCreateFlaggedFolder={handleCreateFlaggedFolder}
                    onSaveAndRecord={handleSaveAndRecordExam}
                    onIncorrectAnswers={handleIncorrectAnswers}
                    onViewSourceCard={handleViewSourceCard}
                />
            )}
            {viewingSourceCards && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/80 backdrop-blur-sm px-4" onClick={() => setViewingSourceCards(null)}>
                    <div
                        className="w-full max-w-md rounded-2xl border border-white/10 bg-slate-900 shadow-2xl overflow-hidden flex flex-col"
                        style={{ maxHeight: '80vh' }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between px-5 py-3.5 border-b border-white/8">
                            <h2 className="text-sm font-semibold text-white">Original flashcard{viewingSourceCards.length !== 1 ? 's' : ''}</h2>
                            <button onClick={() => setViewingSourceCards(null)} className="rounded-full p-1.5 text-slate-400 hover:text-white hover:bg-white/10 transition">
                                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                            </button>
                        </div>
                        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
                            {viewingSourceCards.map((c) => (
                                <div key={c.id} className="rounded-xl bg-slate-800/50 ring-1 ring-white/8 p-3.5">
                                    <p className="text-sm text-slate-200 leading-relaxed"><strong>Q:</strong> {typeof c.question === 'string' ? c.question : JSON.stringify(c.question)}</p>
                                    <p className="text-sm text-slate-300 mt-2 leading-relaxed"><strong>A:</strong> {typeof c.answer === 'string' ? c.answer : JSON.stringify(c.answer)}</p>
                                    {c.sourceDocumentId && (
                                        <button
                                            type="button"
                                            onClick={() => handleViewSourceDocument(c)}
                                            className="mt-3 text-xs rounded-lg px-2.5 py-1 text-teal-300 bg-slate-900/60 ring-1 ring-white/10 hover:bg-teal-500/10 hover:text-teal-200 transition"
                                        >
                                            📄 View source document{c.sourceDocumentPage ? ` (p.${c.sourceDocumentPage})` : ''}
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
            {showExamCreate && (
                <ExamCreateModal
                    allFolders={sortedFolders}
                    savedExams={savedExams}
                    examHistory={examHistory}
                    entitlements={entitlements}
                    preSelectedFolderId={examPreSelectedFolderId}
                    onConfirm={handleCreateExam}
                    onRetake={(examId) => { setShowExamCreate(false); setExamPreSelectedFolderId(null); handleRetakeExam(examId); }}
                    onViewHistory={() => { setShowExamCreate(false); setExamWizardState({ stage: 'history_view' }); }}
                    onClose={() => { setShowExamCreate(false); setExamPreSelectedFolderId(null); }}
                />
            )}
            {examWizardState?.stage === 'history_view' && (
                <ExamHistoryModal
                    examHistory={examHistory}
                    savedExams={savedExams}
                    onRetake={handleRetakeExam}
                    onClose={() => setExamWizardState(null)}
                />
            )}
            {isFeedbackModalOpen && (
                <FeedbackModal onClose={() => setIsFeedbackModalOpen(false)} userEmail={user?.email || ''} />
            )}
            {showImportModal && (
                <ImportModal
                    folders={folders}
                    onImport={handleImportCards}
                    onClose={() => setShowImportModal(false)}
                />
            )}
            {showRestoreBackupModal && (
                <RestoreBackupModal
                    onRestore={handleRestoreBackup}
                    onClose={() => setShowRestoreBackupModal(false)}
                />
            )}
            {showDocumentStudyModal && (documentStudyFile || studyDocument) && (
                <ClinicalDocumentStudyModal
                    file={documentStudyFile}
                    sourceDocument={studyDocument}
                    initialPage={documentStudyInitialPage}
                    folderId={targetFolderId}
                    folders={folders}
                    onFolderCreated={(f) => setTargetFolderId(f.id)}
                    clinicalSettings={clinicalSettings}
                    onCardsGenerated={handleDocumentModalCardsGenerated}
                    onDone={handleDocumentModalDone}
                    onClose={handleDocumentModalDone}
                />
            )}
            {shareFolderTarget && (
                <ShareFolderModal
                    folder={shareFolderTarget}
                    onClose={() => setShareFolderTarget(null)}
                />
            )}
            {/* DocViewer handled globally in App.jsx */}
            {pendingExamSave && (
                <EnterNameModal
                    title="Name this exam"
                    placeholder=""
                    confirmLabel="Save"
                    onClose={() => setPendingExamSave(null)}
                    onConfirm={(name) => {
                        const now = new Date().toISOString();
                        // Keep the backend examId as the saved id so retake can start a server attempt.
                        const examId = pendingExamSave.exam?.examId || pendingExamSave.exam?.id || String(Date.now());
                        const newSaved = { ...pendingExamSave.exam, title: name, id: examId, examId };
                        // Save to Firestore under users/{uid}/savedExams (cross-device)
                        try {
                            const db = getDb();
                            const currentUid = getCurrentUid();
                            if (db && currentUid) {
                                const docRef = doc(db, 'users', currentUid, 'savedExams', String(newSaved.id));
                                setDoc(docRef, { title: name, examId, config: newSaved.config || null, questions: newSaved.questions || [], createdAt: serverTimestamp() }, { merge: true });
                            }
                        } catch (_) { }
                        recordExamResult({
                            examId,
                            title: name,
                            score: pendingExamSave.score,
                            totalQuestions: pendingExamSave.totalQuestions,
                            percentage: pendingExamSave.percentage,
                        }).catch(() => {});
                        // Local cache fallback
                        setSavedExams(prev => [newSaved, ...prev]);
                        setExamHistory(prev => [{ id: newSaved.id, title: name, score: pendingExamSave.percentage, date: now }, ...prev]);
                        setPendingExamSave(null);
                        pushToast('success', `Exam "${name}" saved`);
                    }}
                />
            )}
            {/* Global overlay spinner for long-running operations like Flash Notes */}
            {((isGeneratingNotes && statusMsg) || (isGenerating && statusMsg)) && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/80 backdrop-blur-sm px-4">
                    <div className="flex items-center gap-3 rounded-2xl border border-white/10 bg-slate-900/90 px-4 py-3 shadow-2xl shadow-black/40">
                        <span className="h-5 w-5 rounded-full border-2 border-teal-400 border-t-transparent animate-spin"></span>
                        <span className="text-slate-200 text-sm">{statusMsg}</span>
                    </div>
                    {/* Mobile Review Queue under controls for FlashFoto */}
                </div>
            )}
            {examQueue && (
                <ExamQueuePill
                    status={examQueue.status}
                    onStart={() => {
                        if (examQueue.exam) setActiveExam(examQueue.exam);
                        setExamQueue(null);
                    }}
                    onDismiss={() => setExamQueue(null)}
                />
            )}

            {/* Floating feedback trigger — desktop only (mobile uses footer "Contact Us" link) */}
            <button
                onClick={() => setIsFeedbackModalOpen(true)}
                className="hidden sm:inline-flex fixed bottom-6 left-6 z-30 items-center gap-2 rounded-full bg-white ring-1 ring-slate-200 hover:ring-slate-300 hover:bg-slate-50 dark:bg-slate-800/90 dark:ring-white/12 dark:hover:ring-white/25 dark:hover:bg-slate-700/90 px-3.5 py-2 text-xs font-medium text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white shadow-lg backdrop-blur-sm transition"
                aria-label="Send feedback"
            >
                <svg className="h-3.5 w-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                Feedback
            </button>

            <MobileBottomNav
                onExam={handleStartExam}
                onExamHistory={() => setExamWizardState({ stage: 'history_view' })}
                onAccount={onOpenAccount}
                isExamActive={showExamCreate}
                isHistoryActive={examWizardState?.stage === 'history_view'}
            />
            <div className="toast-container">
                {toasts.map(t => (
                    <div key={t.id} className={`toast ${t.kind}`}>{t.text}</div>
                ))}
            </div>
            {/* Slider visuals handled reactively above; removed legacy script */}
        </div>
    );
};

export default AppHome;
